import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import '@/App.css';
import axios from 'axios';
import Webcam from 'react-webcam';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { MapPin, Calendar, Check, Camera, Sparkles, ArrowRight, Loader2, Clock, X, Phone, Mail, Globe, Search, Package, Users, Settings, FileText, LayoutDashboard, LogOut, ChevronDown, ToggleLeft, ToggleRight, Eye, EyeOff, Edit, Trash2, Plus, Filter, AlertTriangle, CheckCircle, Archive, RefreshCw } from 'lucide-react';

// Import from modular files
import { API, BACKEND_URL } from '@/lib/api';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { AdminAuthProvider, useAdminAuth } from '@/contexts/AdminAuthContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { FeatureGate } from '@/components/common/FeatureGate';
import { Navbar } from '@/components/layout/Navbar';
import { AdminLayout, AdminSidebar } from '@/components/layout/AdminLayout';

// ==================== HOME PAGE ====================

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const checkProfileAndRedirect = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      if (response.data) {
        // Profile exists, go to capture
        navigate('/plan');
      } else {
        // No profile, go to onboarding
        navigate('/onboarding');
      }
    } catch (error) {
      // No profile, go to onboarding
      navigate('/onboarding');
    }
  };

  React.useEffect(() => {
    // If user is logged in, check their profile status
    if (user) {
      checkProfileAndRedirect();
    }
  }, [user, checkProfileAndRedirect]);

  if (user) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-emerald-600" />
            <span className="font-bold text-xl">PepFactor USA</span>
          </div>
          <Button onClick={() => navigate('/auth')} variant="outline" data-testid="signin-button">
            Sign In
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-24 text-center">
        <Badge className="mb-6 bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="hero-badge">
          AI-Powered Appearance Analysis
        </Badge>
        <h1 className="text-6xl font-bold tracking-tight mb-6" data-testid="hero-title">
          Transform Your <span className="text-emerald-600">Hair & Skin</span>
          <br />with Scientific Precision
        </h1>
        <p className="text-xl text-slate-600 mb-12 max-w-3xl mx-auto" data-testid="hero-description">
          Advanced AI captures your baseline, tracks progress, and connects you with certified clinics.
          Real results, verified outcomes.
        </p>
        <div className="flex gap-4 justify-center">
          <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate('/auth')} data-testid="get-started-btn">
            Get Started <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <Button size="lg" variant="outline" data-testid="learn-more-btn">
            Learn More
          </Button>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <Card className="border-2">
            <CardHeader>
              <Camera className="h-12 w-12 text-emerald-600 mb-4" />
              <CardTitle>AI Capture & Analysis</CardTitle>
              <CardDescription>
                Real-time pose and lighting QC. Compute hair density & skin texture indices instantly.
              </CardDescription>
            </CardHeader>
          </Card>
          <Card className="border-2">
            <CardHeader>
              <MapPin className="h-12 w-12 text-emerald-600 mb-4" />
              <CardTitle>Clinic Network</CardTitle>
              <CardDescription>
                Book appointments at certified clinics across LA, NYC, and Miami with $20 deposit.
              </CardDescription>
            </CardHeader>
          </Card>
          <Card className="border-2">
            <CardHeader>
              <Sparkles className="h-12 w-12 text-emerald-600 mb-4" />
              <CardTitle>Maximizer Subscriptions</CardTitle>
              <CardDescription>
                Daily supplements with dynamic delivery cadence based on your adherence.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-slate-50 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
            <div>
              <h3 className="font-semibold text-lg mb-2">For Clinic Owners</h3>
              <p className="text-slate-600 mb-3">Join the PepFactor network and offer cutting-edge AI-powered treatments</p>
              <Button onClick={() => navigate('/auth')} variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                Register Your Clinic
              </Button>
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Contact Us</h3>
              <div className="text-slate-600 space-y-2">
                <p>
                  <span className="font-medium">PepFactor Agent:</span>{' '}
                  <a href="tel:+18788880840" className="text-emerald-600 hover:underline">
                    +1 878 888 0840
                  </a>
                </p>
                <p>
                  <span className="font-medium">Email:</span>{' '}
                  <a href="mailto:info@pepfactor.org" className="text-emerald-600 hover:underline">
                    info@pepfactor.org
                  </a>
                </p>
              </div>
            </div>
          </div>
          <div className="text-center text-slate-600 pt-6 border-t">
            <p>&copy; 2025 PepFactor USA - Aitheria. Appearance-only claims. Not medical advice.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// ==================== AUTH PAGE ====================

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin ? { email, password } : { email, password, name, phone };
      
      const response = await axios.post(`${API}${endpoint}`, payload);
      login(response.data.token, response.data.user);
      toast.success(isLogin ? 'Welcome back!' : 'Account created!');
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-slate-50 p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="h-8 w-8 text-emerald-600" />
            <span className="font-bold text-2xl">PepFactor USA</span>
          </div>
          <CardTitle data-testid="auth-title">{isLogin ? 'Welcome Back' : 'PepFactor USA'}</CardTitle>
          <CardDescription data-testid="auth-description" className="text-base font-medium">
            {isLogin ? 'Sign in to continue your journey' : 'Create Account'}
          </CardDescription>
          <p className="text-sm text-slate-600 mt-1">
            {isLogin ? '' : 'Start your transformation today'}
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
              />
            </div>
            {!isLogin && (
              <div>
                <Label htmlFor="phone">Mobile Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required={!isLogin}
                  placeholder="(555) 123-4567"
                  data-testid="phone-input"
                />
              </div>
            )}
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
              />
            </div>
            <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading} data-testid="auth-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (isLogin ? 'Sign In' : 'Create Account')}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-emerald-600 hover:underline"
              data-testid="auth-toggle-btn"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== ONBOARDING PAGE ====================

// ==================== FITZPATRICK QUIZ MODAL ====================

const FitzQuizModal = ({ open, onClose, onResult }) => {
  const [q1, setQ1] = useState(null);
  const [q2, setQ2] = useState(null);
  const [q3, setQ3] = useState(null);
  const [q4, setQ4] = useState(null);

  const questions = [
    {
      id: 'q1',
      title: 'Unexposed skin tone',
      options: ['Very fair', 'Fair', 'Medium', 'Olive', 'Brown/deep']
    },
    {
      id: 'q2',
      title: 'Sun reaction (1 hour midday)',
      options: ['Always burns', 'Usually burns', 'Sometimes mild burn', 'Rarely burns', 'Never burns']
    },
    {
      id: 'q3',
      title: 'Ability to tan',
      options: ['Never', 'Light', 'Moderate', 'Easily', 'Very easily']
    },
    {
      id: 'q4',
      title: 'Eye / hair (lighter → darker)',
      options: ['Light eyes & light hair', 'Mixed', 'Darker', 'Dark eyes & dark hair', 'Very dark eyes & hair']
    }
  ];

  const classify = (sum) => {
    if (sum <= 2) return 1;
    if (sum <= 5) return 2;
    if (sum <= 8) return 3;
    if (sum <= 11) return 4;
    if (sum <= 14) return 5;
    return 6;
  };

  const handleSubmit = () => {
    if ([q1, q2, q3, q4].some(v => v === null)) {
      toast.error('Please answer all questions');
      return;
    }
    const score = q1 + q2 + q3 + q4;
    const type = classify(score);
    
    // Analytics
    window.dispatchEvent(new CustomEvent('ga_event', { 
      detail: { name: 'fitz_quiz_completed', params: { score, result: type }}
    }));
    
    onResult({ score, type });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6" onClick={onClose}>
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <CardTitle data-testid="fitz-quiz-title">15-second Fitzpatrick Self-Assessment</CardTitle>
          <CardDescription>Answer these questions to determine your skin type</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {questions.map((q, qIdx) => (
            <div key={q.id} className="space-y-3">
              <Label className="text-base font-medium">{qIdx + 1}. {q.title}</Label>
              <div className="space-y-2">
                {q.options.map((option, idx) => (
                  <label key={idx} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name={q.id}
                      checked={
                        (q.id === 'q1' && q1 === idx) ||
                        (q.id === 'q2' && q2 === idx) ||
                        (q.id === 'q3' && q3 === idx) ||
                        (q.id === 'q4' && q4 === idx)
                      }
                      onChange={() => {
                        if (q.id === 'q1') setQ1(idx);
                        if (q.id === 'q2') setQ2(idx);
                        if (q.id === 'q3') setQ3(idx);
                        if (q.id === 'q4') setQ4(idx);
                      }}
                      className="w-4 h-4"
                      data-testid={`${q.id}-option-${idx}`}
                    />
                    <span className="text-sm">{option}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div className="pt-4 border-t">
            <p className="text-xs text-slate-500">
              This self-assessment guides cosmetic personalization only; it is <strong>not</strong> medical advice.
            </p>
          </div>
        </CardContent>
        <CardContent className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1" data-testid="quiz-cancel-btn">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            data-testid="quiz-submit-btn"
          >
            Use my result
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== ONBOARDING PAGE ====================

const OnboardingPage = () => {
  const navigate = useNavigate();
  const [concerns, setConcerns] = useState([]);
  const [fitzpatrick, setFitzpatrick] = useState(null);
  const [fitzSource, setFitzSource] = useState('self_select');
  const [fitzScore, setFitzScore] = useState(null);
  const [contraindications, setContraindications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [profileExists, setProfileExists] = useState(false);

  // Check if profile already exists and load it
  React.useEffect(() => {
    loadExistingProfile();
  }, []);

  const loadExistingProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      if (response.data) {
        // Profile exists, pre-fill the form
        setConcerns(response.data.concerns || []);
        setFitzpatrick(response.data.fitzpatrick_type);
        setFitzSource(response.data.fitz_source || 'self_select');
        setFitzScore(response.data.fitz_score);
        setContraindications(response.data.contraindications || []);
        setProfileExists(true);
      }
    } catch (error) {
      // Profile doesn't exist, that's fine
      if (error.response?.status !== 404) {
        console.error('Failed to load profile:', error);
      }
    }
  };

  const concernOptions = [
    'Hair Thinning', 
    'Hair Loss', 
    'Skin Aging', 
    'Skin Texture', 
    'Fine Lines',
    'Acne (appearance)',
    'Rosacea (appearance)'
  ];
  
  const contraindicationOptions = [
    'Pregnant/Nursing', 
    'Active Skin Condition', 
    'Recent Surgery', 
    'Allergies'
  ];

  const fitzSwatches = [
    { val: 1, label: 'Type I (very fair)', hex: '#FCE9E4' },
    { val: 2, label: 'Type II (fair)', hex: '#F6D7C7' },
    { val: 3, label: 'Type III (medium)', hex: '#E6BFA3' },
    { val: 4, label: 'Type IV (olive/tan)', hex: '#C08E6B' },
    { val: 5, label: 'Type V (brown)', hex: '#8B5E3C' },
    { val: 6, label: 'Type VI (deep brown)', hex: '#5B3A29' }
  ];

  const toggleConcern = (concern) => {
    setConcerns(prev => 
      prev.includes(concern) ? prev.filter(c => c !== concern) : [...prev, concern]
    );
    // Analytics
    window.dispatchEvent(new CustomEvent('ga_event', { 
      detail: { name: 'ob_concerns_selected', params: { values: concerns }}
    }));
  };

  const toggleContraindication = (item) => {
    setContraindications(prev => 
      prev.includes(item) ? prev.filter(c => c !== item) : [...prev, item]
    );
  };

  const handleFitzSelect = (type) => {
    setFitzpatrick(type);
    setFitzSource('self_select');
    setFitzScore(null);
    // Analytics
    window.dispatchEvent(new CustomEvent('ga_event', { 
      detail: { name: 'fitz_selected', params: { type, source: 'self_select' }}
    }));
  };

  const handleQuizResult = (result) => {
    setFitzpatrick(result.type);
    setFitzSource('quiz');
    setFitzScore(result.score);
    setShowQuiz(false);
    toast.success(`Your Fitzpatrick type is ${result.type}`);
    // Analytics
    window.dispatchEvent(new CustomEvent('ga_event', { 
      detail: { name: 'fitz_selected', params: { type: result.type, source: 'quiz' }}
    }));
  };

  const handleSubmit = async () => {
    if (concerns.length === 0) {
      toast.error('Select at least one concern');
      return;
    }
    if (!fitzpatrick) {
      toast.error('Select your Fitzpatrick type or take the 15-second quiz');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/profile`, {
        concerns,
        fitzpatrick_type: fitzpatrick,
        fitz_source: fitzSource,
        fitz_score: fitzScore,
        contraindications,
        region: 'US'
      });
      
      // Analytics
      window.dispatchEvent(new CustomEvent('ga_event', { 
        detail: { name: 'profile_updated' }
      }));
      
      toast.success(profileExists ? 'Profile updated!' : 'Profile created!');
      navigate('/capture');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <Progress value={33} className="h-2 mb-4" />
          <h1 className="text-4xl font-bold mb-2" data-testid="onboarding-title">Let's get started</h1>
          <p className="text-slate-600" data-testid="onboarding-description">
            Tell us about your goals (60 seconds)
            {profileExists && <span className="ml-2 text-emerald-600 font-medium">(Updating your profile)</span>}
          </p>
        </div>

        <div className="space-y-8">
          {/* Concerns */}
          <Card>
            <CardHeader>
              <CardTitle>What are your primary concerns?</CardTitle>
              <CardDescription>Select all that apply</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 mb-3">
                {concernOptions.map(concern => (
                  <div key={concern} className="flex items-center space-x-2">
                    <Checkbox
                      id={concern}
                      checked={concerns.includes(concern)}
                      onCheckedChange={() => toggleConcern(concern)}
                      data-testid={`concern-${concern.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    />
                    <label htmlFor={concern} className="cursor-pointer text-sm">{concern}</label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-4">
                We personalize routines for the <strong>appearance</strong> of skin and scalp; this is not a medical diagnosis.
              </p>
            </CardContent>
          </Card>

          {/* Fitzpatrick with Swatches */}
          <Card>
            <CardHeader>
              <CardTitle>Fitzpatrick Skin Type (I–VI)</CardTitle>
              <CardDescription>
                Select your skin tone. {' '}
                <button 
                  type="button" 
                  onClick={() => setShowQuiz(true)}
                  className="text-emerald-600 hover:text-emerald-700 underline font-medium"
                  data-testid="take-quiz-btn"
                >
                  Not sure? Take a 15-second quiz
                </button>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {fitzSwatches.map(swatch => (
                  <label 
                    key={swatch.val} 
                    className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      fitzpatrick === swatch.val 
                        ? 'border-emerald-600 bg-emerald-50' 
                        : 'border-slate-200 hover:border-emerald-300'
                    }`}
                    data-testid={`fitzpatrick-swatch-${swatch.val}`}
                  >
                    <input
                      type="radio"
                      name="fitzpatrick"
                      value={swatch.val}
                      checked={fitzpatrick === swatch.val}
                      onChange={() => handleFitzSelect(swatch.val)}
                      className="sr-only"
                    />
                    <div 
                      className="w-10 h-10 rounded-full border-2 border-slate-300 shrink-0" 
                      style={{ backgroundColor: swatch.hex }}
                      aria-hidden="true"
                    />
                    <span className="text-xs font-medium">{swatch.label}</span>
                  </label>
                ))}
              </div>
              {fitzSource === 'quiz' && fitzScore !== null && (
                <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-sm text-emerald-800">
                    ✓ Quiz result: Type {fitzpatrick} (score: {fitzScore})
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contraindications */}
          <Card>
            <CardHeader>
              <CardTitle>Any contraindications? <span className="text-slate-400 font-normal text-sm">(optional)</span></CardTitle>
              <CardDescription>Helps us personalize your plan</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {contraindicationOptions.map(item => (
                  <div key={item} className="flex items-center space-x-2">
                    <Checkbox
                      id={item}
                      checked={contraindications.includes(item)}
                      onCheckedChange={() => toggleContraindication(item)}
                      data-testid={`contraindication-${item.toLowerCase().replace(/\s+/g, '-')}`}
                    />
                    <label htmlFor={item} className="cursor-pointer text-sm">{item}</label>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Button
            size="lg"
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            onClick={handleSubmit}
            disabled={loading}
            data-testid="onboarding-continue-btn"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Continue to Capture'}
          </Button>

          <div className="text-center">
            <a href="#" className="text-sm text-slate-500 hover:text-slate-700 underline">
              Claims & Safety (appearance-only policy)
            </a>
          </div>
        </div>
      </div>

      {/* Quiz Modal */}
      <FitzQuizModal
        open={showQuiz}
        onClose={() => setShowQuiz(false)}
        onResult={handleQuizResult}
      />
    </div>
  );
};

// ==================== CAPTURE PAGE ====================

const CapturePage = () => {
  const navigate = useNavigate();
  const webcamRef = React.useRef(null);
  const [image, setImage] = useState(null);
  const [qcResult, setQcResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [webcamError, setWebcamError] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // 'user' or 'environment'
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  React.useEffect(() => {
    const checkMobile = () => {
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(mobile);
    };
    checkMobile();
  }, []);

  // Request camera permission on mount
  React.useEffect(() => {
    requestCameraPermission();
  }, [facingMode]);

  const requestCameraPermission = async () => {
    try {
      const constraints = {
        video: { 
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      // Stop the test stream immediately
      stream.getTracks().forEach(track => track.stop());
      setHasPermission(true);
      setWebcamError(null);
    } catch (error) {
      console.error('Camera permission error:', error);
      setHasPermission(false);
      if (error.name === 'NotAllowedError') {
        setWebcamError('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        setWebcamError('No camera found. Please connect a camera and refresh.');
      } else {
        setWebcamError('Unable to access camera. Please check browser permissions.');
      }
    }
  };

  const handleWebcamError = (error) => {
    console.error('Webcam error:', error);
    setWebcamError('Camera error. Please refresh and try again.');
    setHasPermission(false);
  };

  const toggleCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    setImage(null);
    setQcResult(null);
  };

  const captureImage = React.useCallback(() => {
    try {
      if (!webcamRef.current) {
        toast.error('Camera not ready');
        return;
      }
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) {
        toast.error('Failed to capture image. Please try again.');
        return;
      }
      setImage(imageSrc);
      checkQC(imageSrc);
    } catch (error) {
      console.error('Capture error:', error);
      toast.error('Failed to capture photo');
    }
  }, [webcamRef]);

  const checkQC = async (imageData) => {
    setLoading(true);
    try {
      const base64 = imageData.split(',')[1];
      const response = await axios.post(`${API}/capture/qc`, {
        image_base64: base64
      });
      setQcResult(response.data);
      
      if (response.data.pass) {
        toast.success('Perfect! Photo quality passed.');
      } else {
        toast.warning('Photo quality needs improvement. See suggestions below.');
      }
    } catch (error) {
      console.error('QC error:', error);
      toast.error('QC check failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getDeviceInfo = () => {
    return {
      user_agent: navigator.userAgent,
      platform: navigator.platform,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      device_pixel_ratio: window.devicePixelRatio || 1
    };
  };

  const uploadPhoto = async () => {
    if (!image || !qcResult?.pass) {
      toast.error('Please capture a passing photo first');
      return;
    }

    setUploading(true);
    try {
      const base64 = image.split(',')[1];
      const response = await axios.post(`${API}/capture/upload`, {
        image_base64: base64,
        type: 'baseline',
        device_info: getDeviceInfo()
      });
      
      toast.success('Photo uploaded! Analyzing your baseline...');
      // Navigate to results page with photo data
      navigate('/results', { state: { photoData: response.data } });
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <Progress value={66} className="h-2 mb-4" />
          <h1 className="text-4xl font-bold mb-2" data-testid="capture-title">AI-Powered Capture</h1>
          <p className="text-slate-600" data-testid="capture-description">Take your baseline photo with real-time quality check</p>
          <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
            <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full"></span>
            <span>ProofGuard: In-app camera only. No filters or edits.</span>
          </div>
        </div>

        {/* Permission Alert */}
        {hasPermission === false && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Camera className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-900 mb-1">Camera Access Required</h3>
                  <p className="text-sm text-amber-800 mb-3">{webcamError}</p>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={requestCameraPermission}
                    className="border-amber-600 text-amber-700 hover:bg-amber-100"
                    data-testid="retry-camera-btn"
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Camera */}
          <Card>
            <CardContent className="p-6">
              {!image ? (
                <div className="space-y-4">
                  {hasPermission === true ? (
                    <>
                      <div className="rounded-lg overflow-hidden bg-slate-100 relative">
                        <Webcam
                          ref={webcamRef}
                          screenshotFormat="image/jpeg"
                          className="w-full"
                          videoConstraints={{
                            facingMode: facingMode,
                            width: 1280,
                            height: 720
                          }}
                          onUserMediaError={handleWebcamError}
                          data-testid="webcam-feed"
                        />
                        {/* Camera toggle button (mobile only) */}
                        {isMobile && (
                          <button
                            onClick={toggleCamera}
                            className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-full shadow-lg hover:bg-white"
                            data-testid="toggle-camera-btn"
                          >
                            <svg className="h-6 w-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                        )}
                      </div>
                      <Button
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        onClick={captureImage}
                        data-testid="capture-photo-btn"
                      >
                        <Camera className="mr-2 h-5 w-5" /> Capture Photo
                      </Button>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <Camera className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                      <p className="text-slate-500 mb-4">
                        {hasPermission === null ? 'Requesting camera access...' : 'Camera not available'}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <img src={image} alt="Captured" className="rounded-lg w-full" data-testid="captured-image" />
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => { 
                        setImage(null); 
                        setQcResult(null); 
                      }} 
                      className="flex-1" 
                      data-testid="retake-photo-btn"
                    >
                      Retake
                    </Button>
                    <Button
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      onClick={uploadPhoto}
                      disabled={!qcResult?.pass || uploading}
                      data-testid="upload-photo-btn"
                    >
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Upload & Analyze'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* QC Results */}
          <Card>
            <CardHeader>
              <CardTitle>Quality Check</CardTitle>
              <CardDescription>Real-time AI validation</CardDescription>
            </CardHeader>
            <CardContent>
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                </div>
              )}
              
              {qcResult && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Lighting Score</span>
                    <span className="text-2xl font-bold" data-testid="lighting-score">{qcResult.lighting_score}/100</span>
                  </div>
                  <Progress value={qcResult.lighting_score} className="h-2" />
                  
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Pose Score</span>
                    <span className="text-2xl font-bold" data-testid="pose-score">{qcResult.pose_score}/100</span>
                  </div>
                  <Progress value={qcResult.pose_score} className="h-2" />
                  
                  <div className="pt-4 border-t">
                    <div className="flex items-center gap-2 mb-3">
                      {qcResult.pass ? (
                        <Badge className="bg-emerald-100 text-emerald-700" data-testid="qc-pass-badge">
                          <Check className="h-4 w-4 mr-1" /> Passed
                        </Badge>
                      ) : (
                        <Badge variant="destructive" data-testid="qc-fail-badge">Failed</Badge>
                      )}
                    </div>
                    <div className="space-y-2">
                      {qcResult.suggestions?.map((suggestion, idx) => (
                        <p key={idx} className="text-sm text-slate-600" data-testid={`qc-suggestion-${idx}`}>
                          • {suggestion}
                        </p>
                      ))}
                    </div>
                  </div>

                  {qcResult.metadata && (
                    <div className="pt-4 border-t">
                      <p className="text-xs text-slate-500">
                        QC Version: {qcResult.metadata.qc_version} • 
                        Fitzpatrick Type: {qcResult.metadata.fitzpatrick_type || 'III'}
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {!loading && !qcResult && (
                <div className="text-center py-12 text-slate-400">
                  <Camera className="h-16 w-16 mx-auto mb-4" />
                  <p>Capture a photo to see quality check results</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ==================== RESULTS PAGE (POST-CAPTURE) ====================

const ResultsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    loadProfileAndRecommendations();
  }, []);

  const loadProfileAndRecommendations = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setProfile(response.data);
      
      // Generate recommendations based on concerns
      const recs = generateRecommendations(response.data.concerns, response.data.fitzpatrick_type);
      setRecommendations(recs);
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  const generateRecommendations = (concerns, fitzType) => {
    const recommendations = [];
    const concernCodes = concerns || [];

    // Hair concerns
    if (concernCodes.includes('hair_thinning') || concernCodes.includes('hair_loss')) {
      recommendations.push({
        type: 'product',
        title: 'Hair Maximizer Starter Pack',
        description: 'Scientifically formulated blend to support hair density and scalp health. Contains biotin, saw palmetto, and peptides.',
        reason: 'Based on your hair thinning concerns, this formula targets follicle health and growth cycle support.',
        sku: 'max_hair_starter_90',
        action: 'Start Subscription'
      });
    }

    // Skin concerns
    if (concernCodes.includes('skin_aging') || concernCodes.includes('fine_lines') || concernCodes.includes('skin_texture')) {
      recommendations.push({
        type: 'product',
        title: 'Skin Maximizer for Texture & Tone',
        description: 'Daily supplement with collagen, hyaluronic acid, and antioxidants for skin appearance.',
        reason: 'Addresses skin texture and appearance concerns you selected. Formulated for visible results in 30-90 days.',
        sku: 'max_skin_w_starter_90',
        action: 'Start Subscription'
      });
    }

    // Acne/Rosacea concerns
    if (concernCodes.includes('acne') || concernCodes.includes('rosacea')) {
      recommendations.push({
        type: 'clinic',
        title: 'Professional Consultation Recommended',
        description: 'For acne and rosacea concerns, an in-person consultation can provide targeted treatment options.',
        reason: 'Clinical assessment allows for personalized treatment plans including topicals, procedures, or professional-grade products.',
        action: 'Book Clinic Visit'
      });
    }

    // Fitzpatrick-specific
    if (fitzType <= 2) {
      recommendations.push({
        type: 'tip',
        title: 'Sun Protection (Type I-II)',
        description: 'Your Fitzpatrick type indicates higher sensitivity to sun exposure.',
        reason: 'Consider broad-spectrum SPF 50+ daily and avoid peak sun hours. Discuss vitamin D supplementation with your provider.',
        action: null
      });
    } else if (fitzType >= 5) {
      recommendations.push({
        type: 'tip',
        title: 'Hyperpigmentation Monitoring (Type V-VI)',
        description: 'Your skin type may be more prone to post-inflammatory hyperpigmentation.',
        reason: 'Track any dark spots after inflammation. Consider products with niacinamide or vitamin C for evening tone.',
        action: null
      });
    }

    // Default if no specific recommendations
    if (recommendations.length === 0) {
      recommendations.push({
        type: 'general',
        title: 'Personalized Plan Available',
        description: 'Based on your baseline capture, we can create a customized plan.',
        reason: 'Schedule a consultation to discuss your goals and create a targeted approach.',
        action: 'Book Consultation'
      });
    }

    return recommendations;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Success Banner */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
            <Check className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="text-4xl font-bold mb-2" data-testid="results-title">Baseline Captured!</h1>
          <p className="text-xl text-slate-600">AI analysis complete. Here's your personalized plan.</p>
        </div>

        {/* Recommendations */}
        <div className="space-y-6 mb-12">
          <h2 className="text-2xl font-bold">Recommended for You</h2>
          
          {recommendations.map((rec, idx) => (
            <Card key={idx} className={`border-2 ${rec.type === 'product' ? 'border-emerald-200' : 'border-slate-200'}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-xl mb-2">{rec.title}</CardTitle>
                    <CardDescription className="text-base">{rec.description}</CardDescription>
                  </div>
                  {rec.type === 'product' && <Badge className="bg-emerald-100 text-emerald-700">Recommended</Badge>}
                  {rec.type === 'clinic' && <Badge className="bg-blue-100 text-blue-700">Consultation</Badge>}
                  {rec.type === 'tip' && <Badge className="bg-amber-100 text-amber-700">Tip</Badge>}
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4 p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-1">Why this recommendation?</p>
                  <p className="text-sm text-slate-600">{rec.reason}</p>
                </div>
                {rec.action && (
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => {
                      if (rec.action === 'Start Subscription') {
                        navigate('/subscriptions');
                      } else if (rec.action === 'Book Clinic Visit' || rec.action === 'Book Consultation') {
                        navigate('/book');
                      }
                    }}
                    data-testid={`action-${idx}`}
                  >
                    {rec.action} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Next Steps */}
        <Card className="border-2 border-emerald-200 bg-emerald-50">
          <CardHeader>
            <CardTitle>What Happens Next?</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="flex items-center justify-center w-6 h-6 bg-emerald-600 text-white rounded-full text-sm font-bold shrink-0">1</span>
                <div>
                  <p className="font-medium">Choose Your Path</p>
                  <p className="text-sm text-slate-600">Start a subscription or book a clinic consultation based on recommendations above.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex items-center justify-center w-6 h-6 bg-emerald-600 text-white rounded-full text-sm font-bold shrink-0">2</span>
                <div>
                  <p className="font-medium">Track Progress</p>
                  <p className="text-sm text-slate-600">Take Day 30 and Day 90 photos to see changes. AI will compare against your baseline.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex items-center justify-center w-6 h-6 bg-emerald-600 text-white rounded-full text-sm font-bold shrink-0">3</span>
                <div>
                  <p className="font-medium">Get Outcomes Report</p>
                  <p className="text-sm text-slate-600">Receive AI-generated reports showing visible changes in hair density, skin texture, and more.</p>
                </div>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* CTA Buttons */}
        <div className="mt-8 space-y-4">
          <Button
            size="lg"
            className="w-full bg-blue-600 hover:bg-blue-700"
            onClick={() => navigate('/analysis/professional', { state: { photoData: location.state?.photoData } })}
          >
            <Sparkles className="mr-2 h-5 w-5" />
            Get Professional Analysis & Treatment Plan
          </Button>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/book')}
              data-testid="book-clinic-cta"
            >
              <MapPin className="mr-2 h-5 w-5" />
              Book Clinic Visit
            </Button>
            <Button
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => navigate('/subscriptions')}
              data-testid="view-products-cta"
            >
              <Sparkles className="mr-2 h-5 w-5" />
              View Products
            </Button>
          </div>
        </div>

        {/* Skip for now */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/plan')}
            className="text-sm text-slate-500 hover:text-slate-700 underline"
          >
            Skip for now, explore app
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== PLAN PAGE (DUAL CTA) ====================

const PlanPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-12 text-center">
          <Progress value={100} className="h-2 mb-4 max-w-md mx-auto" />
          <Badge className="mb-4 bg-emerald-100 text-emerald-700" data-testid="plan-badge">
            <Check className="h-4 w-4 mr-1" /> Baseline Captured
          </Badge>
          <h1 className="text-5xl font-bold mb-4" data-testid="plan-title">Your Personalized Plan</h1>
          <p className="text-xl text-slate-600" data-testid="plan-description">
            Based on your AI analysis, here are your best next steps
          </p>
        </div>

        {/* Dual CTA Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Book Clinic */}
          <Card className="border-2 border-emerald-200 hover:border-emerald-400 transition-all hover:shadow-xl">
            <CardHeader>
              <div className="h-12 w-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <MapPin className="h-6 w-6 text-emerald-600" />
              </div>
              <CardTitle className="text-2xl">Book a Clinic Visit</CardTitle>
              <CardDescription className="text-base">
                Get expert consultation at a certified PepFactor clinic. $20 deposit (applies to treatment).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0" />
                  <span>Professional assessment & consultation</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0" />
                  <span>Advanced treatments available</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0" />
                  <span>Day 30 & 90 progress tracking</span>
                </li>
              </ul>
              <Button
                size="lg"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate('/book')}
                data-testid="book-clinic-btn"
              >
                Find Clinics <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </CardContent>
          </Card>

          {/* Start Subscription */}
          <Card className="border-2 border-emerald-200 hover:border-emerald-400 transition-all hover:shadow-xl">
            <CardHeader>
              <div className="h-12 w-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <Sparkles className="h-6 w-6 text-emerald-600" />
              </div>
              <CardTitle className="text-2xl">Start Maximizer Subscription</CardTitle>
              <CardDescription className="text-base">
                Daily supplements delivered to your door. Dynamic cadence based on adherence.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0" />
                  <span>Science-backed formulations</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0" />
                  <span>AI-optimized delivery schedule</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-emerald-600 shrink-0" />
                  <span>Pause or cancel anytime</span>
                </li>
              </ul>
              <Button
                size="lg"
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate('/subscriptions')}
                data-testid="start-subscription-btn"
              >
                View Products <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Info */}
        <div className="text-center text-sm text-slate-500">
          <p>Both options include AI progress tracking and Day 30/90 outcome reports</p>
        </div>
      </div>
    </div>
  );
};

// ==================== BOOK PAGE ====================

const BookPage = () => {
  const navigate = useNavigate();
  const [clinics, setClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetchClinics();
  }, []);

  const fetchClinics = async () => {
    try {
      const response = await axios.get(`${API}/clinics`);
      setClinics(response.data);
    } catch (error) {
      toast.error('Failed to load clinics');
    } finally {
      setLoading(false);
    }
  };

  const selectClinic = (clinic) => {
    setSelectedClinic(clinic);
    navigate(`/book/${clinic.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" data-testid="book-title">Find a Clinic</h1>
          <p className="text-slate-600" data-testid="book-description">Select a location for your consultation</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clinics.map(clinic => (
            <Card key={clinic.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => selectClinic(clinic)}>
              <CardHeader>
                <CardTitle className="text-lg" data-testid={`clinic-name-${clinic.id}`}>{clinic.name}</CardTitle>
                <CardDescription>
                  <div className="flex items-start gap-2 mt-2">
                    <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
                    <span className="text-sm">
                      {clinic.address}<br />
                      {clinic.city}, {clinic.state} {clinic.zip_code}
                    </span>
                  </div>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid={`select-clinic-${clinic.id}`}>
                  Select Clinic <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// ==================== CLINIC DETAIL PAGE ====================

const ClinicDetailPage = () => {
  const navigate = useNavigate();
  const { clinicId } = useParams();
  const [clinic, setClinic] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);

  React.useEffect(() => {
    fetchClinicDetails();
  }, [clinicId]);

  const fetchClinicDetails = async () => {
    try {
      const clinicResponse = await axios.get(`${API}/clinics/${clinicId}`);
      setClinic(clinicResponse.data);

      // Fetch available slots (next 7 days)
      const today = new Date();
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);

      const startDate = today.toISOString().split('T')[0];
      const endDate = nextWeek.toISOString().split('T')[0];

      try {
        const slotsResponse = await axios.get(`${API}/clinics/${clinicId}/slots?start_date=${startDate}&end_date=${endDate}`);
        setSlots(slotsResponse.data.slots || []);
      } catch (error) {
        console.error('Failed to load slots:', error);
        // Don't fail the whole page if slots fail
        setSlots([]);
      }
    } catch (error) {
      toast.error('Failed to load clinic details');
      navigate('/book');
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = () => {
    if (!selectedSlot) {
      toast.error('Please select a time slot');
      return;
    }
    // Navigate to appointment confirmation with slot info
    navigate('/book/confirm', { state: { clinic, slot: selectedSlot } });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!clinic) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Clinic not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate('/book')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
        >
          <ArrowRight className="h-4 w-4 rotate-180" />
          Back to All Clinics
        </button>

        {/* Clinic Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-3xl" data-testid="clinic-detail-name">{clinic.name}</CardTitle>
                <CardDescription className="text-base">
                  <div className="flex items-start gap-2 mt-3">
                    <MapPin className="h-5 w-5 shrink-0 mt-0.5 text-emerald-600" />
                    <div>
                      <p>{clinic.address}</p>
                      <p>{clinic.city}, {clinic.state} {clinic.zip_code}</p>
                    </div>
                  </div>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2">About This Location</h3>
                    <p className="text-sm text-slate-600">
                      PepFactor certified clinic offering professional consultations, advanced treatments, and progress tracking. 
                      Our specialists use your AI baseline to create personalized treatment plans.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">What's Included</h3>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-emerald-600 mt-0.5" />
                        <span>30-minute consultation with certified specialist</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-emerald-600 mt-0.5" />
                        <span>Personalized treatment plan based on AI analysis</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-emerald-600 mt-0.5" />
                        <span>$20 deposit (applies to treatment cost)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-emerald-600 mt-0.5" />
                        <span>Day 30 & Day 90 progress tracking photos</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Booking Card */}
          <div>
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>Book Appointment</CardTitle>
                <CardDescription>$20 deposit required</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Available Time Slots</Label>
                    {slots.length > 0 ? (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {slots.slice(0, 10).map((slot, idx) => (
                          <button
                            key={idx}
                            onClick={() => setSelectedSlot(slot)}
                            className={`w-full p-3 text-left rounded-lg border-2 transition-all ${
                              selectedSlot?.start === slot.start
                                ? 'border-emerald-600 bg-emerald-50'
                                : 'border-slate-200 hover:border-emerald-300'
                            }`}
                            data-testid={`slot-${idx}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-sm">
                                  {new Date(slot.start).toLocaleDateString('en-US', { 
                                    weekday: 'short', 
                                    month: 'short', 
                                    day: 'numeric' 
                                  })}
                                </p>
                                <p className="text-xs text-slate-600">
                                  {new Date(slot.start).toLocaleTimeString('en-US', { 
                                    hour: 'numeric', 
                                    minute: '2-digit' 
                                  })}
                                </p>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {slot.event_type === 'consult_30' ? '30 min' : '60 min'}
                              </Badge>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-800">
                          Online booking temporarily unavailable. Please call clinic directly to schedule.
                        </p>
                      </div>
                    )}
                  </div>

                  {slots.length > 0 && (
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      onClick={handleBooking}
                      disabled={!selectedSlot}
                      data-testid="confirm-booking-btn"
                    >
                      Continue to Payment
                    </Button>
                  )}

                  <div className="text-xs text-slate-500 text-center">
                    <p>Cancel or reschedule up to 48 hours before appointment for full refund.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== SHOP PAGE (Consumer Products) ====================

const PRODUCT_TYPE_LABELS = {
  'CONSUMABLE': 'Products',
  'HOME_DEVICE': 'Devices',
  'SERVICE': 'Services',
  'SUBSCRIPTION': 'Subscriptions'
};

const PRODUCT_TYPE_ICONS = {
  'CONSUMABLE': '🧴',
  'HOME_DEVICE': '💡',
  'SERVICE': '✨',
  'SUBSCRIPTION': '📦'
};

const ShopPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState('all');
  const [enquiryProduct, setEnquiryProduct] = useState(null);
  const [enquiryForm, setEnquiryForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?category=consumer`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = (product) => {
    if (!product.is_purchasable) {
      setEnquiryProduct(product);
      return;
    }
    
    if (product.checkout_mode === 'enquiry_only') {
      setEnquiryProduct(product);
      return;
    }
    
    if (product.checkout_mode === 'stripe_payment_link' && product.payment_link_url) {
      window.open(product.payment_link_url, '_blank');
      return;
    }
    
    toast.info('Checkout coming soon');
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', enquiryForm.name);
      formData.append('email', enquiryForm.email);
      formData.append('phone', enquiryForm.phone || '');
      formData.append('message', enquiryForm.message || '');
      formData.append('enquiry_type', enquiryProduct.product_type === 'SERVICE' ? 'info_request' : 'demo_request');

      await axios.post(`${API}/products/${enquiryProduct.id}/enquiry`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Enquiry submitted! We will contact you within 24-48 hours.');
      setEnquiryProduct(null);
      setEnquiryForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredProducts = selectedType === 'all' 
    ? products 
    : products.filter(p => p.product_type === selectedType);

  const productTypes = [...new Set(products.map(p => p.product_type))];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Shop PepFactor</h1>
          <p className="text-slate-600">Professional-grade products for your wellness journey</p>
        </div>

        {/* Category Filter */}
        <div className="mb-8 flex flex-wrap gap-2">
          <Button
            variant={selectedType === 'all' ? 'default' : 'outline'}
            onClick={() => setSelectedType('all')}
            className={selectedType === 'all' ? 'bg-emerald-600' : ''}
          >
            All Products
          </Button>
          {productTypes.map(type => (
            <Button
              key={type}
              variant={selectedType === type ? 'default' : 'outline'}
              onClick={() => setSelectedType(type)}
              className={selectedType === type ? 'bg-emerald-600' : ''}
            >
              {PRODUCT_TYPE_ICONS[type]} {PRODUCT_TYPE_LABELS[type] || type}
            </Button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map(product => (
            <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              {product.image_url && (
                <img src={product.image_url} alt={product.name} className="w-full h-48 object-cover" />
              )}
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="outline" className="mb-2">
                      {PRODUCT_TYPE_ICONS[product.product_type]} {PRODUCT_TYPE_LABELS[product.product_type] || product.product_type}
                    </Badge>
                    <CardTitle className="text-lg">{product.name}</CardTitle>
                  </div>
                  {product.has_fda_clearance && (
                    <Badge className="bg-blue-100 text-blue-800">FDA Cleared</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4 line-clamp-2">{product.description}</p>
                
                {/* Device Claims Warning */}
                {product.product_type === 'HOME_DEVICE' && (
                  <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded mb-4">
                    <p className="font-medium mb-1">Important:</p>
                    <p>{product.computed_claims?.US?.[0] || 'For personal wellness use only'}</p>
                  </div>
                )}
                
                {/* Device Specs */}
                {product.device_specs && (
                  <div className="text-xs text-slate-500 mb-4">
                    {product.device_specs.wavelengths && (
                      <span className="inline-block bg-slate-100 rounded px-2 py-1 mr-1 mb-1">
                        {product.device_specs.wavelengths.length} wavelengths
                      </span>
                    )}
                    {product.certifications?.map(cert => (
                      <span key={cert} className="inline-block bg-slate-100 rounded px-2 py-1 mr-1 mb-1">
                        {cert}
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div>
                    {product.price > 0 ? (
                      <span className="text-2xl font-bold text-emerald-600">${product.price.toFixed(2)}</span>
                    ) : (
                      <span className="text-lg font-medium text-slate-600">Price on Request</span>
                    )}
                  </div>
                  <Button 
                    onClick={() => handleBuyNow(product)}
                    className={product.is_purchasable ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-600 hover:bg-slate-700'}
                  >
                    {product.is_purchasable && product.checkout_mode !== 'enquiry_only' ? 'Buy Now' : 
                     product.product_type === 'SERVICE' ? 'Book Now' : 'Request Demo'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">🛍️</span>
              </div>
              <h2 className="text-2xl font-bold mb-4">No Products Found</h2>
              <p className="text-slate-600">Check back soon for new products!</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Enquiry Modal */}
      {enquiryProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>
                {enquiryProduct.product_type === 'SERVICE' ? 'Book ' : 'Request Demo: '}
                {enquiryProduct.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEnquirySubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Name *</label>
                  <Input
                    required
                    value={enquiryForm.name}
                    onChange={(e) => setEnquiryForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <Input
                    type="email"
                    required
                    value={enquiryForm.email}
                    onChange={(e) => setEnquiryForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <Input
                    value={enquiryForm.phone}
                    onChange={(e) => setEnquiryForm(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Message</label>
                  <textarea
                    className="w-full p-2 border rounded-lg min-h-[80px]"
                    value={enquiryForm.message}
                    onChange={(e) => setEnquiryForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Tell us about your needs..."
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setEnquiryProduct(null)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={submitting}>
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Submit
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Product Detail Page
const ProductDetailPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [selectedMarket, setSelectedMarket] = useState('US');

  React.useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      toast.error('Product not found');
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyNow = () => {
    if (!product.is_purchasable || product.checkout_mode === 'enquiry_only') {
      setShowEnquiry(true);
      return;
    }
    
    if (product.checkout_mode === 'stripe_payment_link' && product.payment_link_url) {
      window.open(product.payment_link_url, '_blank');
      return;
    }
    
    toast.info('Checkout coming soon');
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(enquiryForm).forEach(([k, v]) => formData.append(k, v || ''));
      formData.append('enquiry_type', product.product_type === 'SERVICE' ? 'info_request' : 'demo_request');

      await axios.post(`${API}/products/${product.id}/enquiry`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Enquiry submitted! We will contact you within 24-48 hours.');
      setShowEnquiry(false);
      setEnquiryForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <Button variant="ghost" onClick={() => navigate('/shop')} className="mb-6">
          ← Back to Shop
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div>
            {product.image_url ? (
              <img src={product.image_url} alt={product.name} className="w-full rounded-lg shadow-lg" />
            ) : (
              <div className="w-full aspect-square bg-slate-100 rounded-lg flex items-center justify-center">
                <span className="text-6xl">{PRODUCT_TYPE_ICONS[product.product_type] || '📦'}</span>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="outline">
                {PRODUCT_TYPE_ICONS[product.product_type]} {PRODUCT_TYPE_LABELS[product.product_type]}
              </Badge>
              {product.has_fda_clearance && (
                <Badge className="bg-blue-100 text-blue-800">
                  FDA {product.fda_status?.replace('_', ' ')} ({product.fda_k_number})
                </Badge>
              )}
            </div>

            <h1 className="text-3xl font-bold mb-4">{product.name}</h1>
            <p className="text-slate-600 mb-6">{product.description}</p>

            {/* Price & Buy */}
            <div className="bg-slate-50 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                {product.price > 0 ? (
                  <span className="text-3xl font-bold text-emerald-600">${product.price.toFixed(2)}</span>
                ) : (
                  <span className="text-xl font-medium text-slate-600">Price on Request</span>
                )}
              </div>
              <Button onClick={handleBuyNow} className="w-full bg-emerald-600 hover:bg-emerald-700" size="lg">
                {product.is_purchasable && product.checkout_mode !== 'enquiry_only' ? 'Buy Now' : 
                 product.product_type === 'SERVICE' ? 'Book Now' : 'Request Demo'}
              </Button>
            </div>

            {/* Device Specifications */}
            {product.device_specs && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg">Specifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-2 gap-4 text-sm">
                    {Object.entries(product.device_specs).map(([key, value]) => (
                      <div key={key}>
                        <dt className="font-medium text-slate-500 capitalize">{key.replace(/_/g, ' ')}</dt>
                        <dd className="text-slate-900">
                          {Array.isArray(value) ? value.join(', ') : String(value)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </CardContent>
              </Card>
            )}

            {/* Claims & Disclaimers (for devices) */}
            {product.product_type === 'HOME_DEVICE' && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-lg">Important Information</CardTitle>
                  <div className="flex gap-2 mt-2">
                    {['US', 'AU', 'NZ'].map(market => (
                      <Button
                        key={market}
                        size="sm"
                        variant={selectedMarket === market ? 'default' : 'outline'}
                        onClick={() => setSelectedMarket(market)}
                        className={selectedMarket === market ? 'bg-emerald-600' : ''}
                      >
                        {market}
                      </Button>
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  {product.intended_use_text && (
                    <div className="mb-4">
                      <h4 className="font-medium mb-1">Intended Use</h4>
                      <p className="text-sm text-slate-600">{product.intended_use_text}</p>
                    </div>
                  )}
                  
                  <div className="mb-4">
                    <h4 className="font-medium mb-1">Claims ({selectedMarket})</h4>
                    <ul className="text-sm text-slate-600 space-y-1">
                      {(product.computed_claims?.[selectedMarket] || []).map((claim, i) => (
                        <li key={i}>• {claim}</li>
                      ))}
                    </ul>
                  </div>

                  {product.warnings_and_contraindications && product.warnings_and_contraindications.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h4 className="font-medium text-amber-800 mb-2">⚠️ Warnings & Contraindications</h4>
                      <ul className="text-sm text-amber-700 space-y-1">
                        {product.warnings_and_contraindications.map((warning, i) => (
                          <li key={i}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Certifications */}
            {product.certifications && product.certifications.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Certifications:</span>
                {product.certifications.map(cert => (
                  <Badge key={cert} variant="outline">{cert}</Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enquiry Modal */}
      {showEnquiry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>
                {product.product_type === 'SERVICE' ? 'Book ' : 'Request Demo: '}
                {product.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleEnquirySubmit} className="space-y-4">
                <Input required placeholder="Your Name *" value={enquiryForm.name} onChange={(e) => setEnquiryForm(prev => ({ ...prev, name: e.target.value }))} />
                <Input type="email" required placeholder="Email *" value={enquiryForm.email} onChange={(e) => setEnquiryForm(prev => ({ ...prev, email: e.target.value }))} />
                <Input placeholder="Phone" value={enquiryForm.phone} onChange={(e) => setEnquiryForm(prev => ({ ...prev, phone: e.target.value }))} />
                <textarea className="w-full p-2 border rounded-lg min-h-[80px]" placeholder="Message" value={enquiryForm.message} onChange={(e) => setEnquiryForm(prev => ({ ...prev, message: e.target.value }))} />
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowEnquiry(false)} className="flex-1">Cancel</Button>
                  <Button type="submit" className="flex-1 bg-emerald-600" disabled={submitting}>
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Submit
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// ==================== ADMIN PORTAL (NEW RBAC SYSTEM) ====================

// Note: AdminAuthContext, useAdminAuth, AdminAuthProvider are now imported from @/contexts/AdminAuthContext

// ==================== ORIGINAL ADMIN PRODUCTS PAGE (kept for backwards compat) ====================

const AdminProductsPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  React.useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, { headers });
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API}/admin/products`, productData, { headers });
        toast.success('Product created successfully');
      }
      
      setShowForm(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm('Are you sure you want to deactivate this product?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/products/${productId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Product deactivated');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to deactivate product');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold mb-2">Product Management</h1>
            <p className="text-slate-600">Manage consumer and provider products</p>
          </div>
          <Button
            onClick={() => {
              setEditingProduct(null);
              setShowForm(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            Add New Product
          </Button>
        </div>

        {showForm && (
          <ProductForm
            product={editingProduct}
            onSave={handleSaveProduct}
            onCancel={() => {
              setShowForm(false);
              setEditingProduct(null);
            }}
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {products.map(product => (
            <Card key={product.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{product.name}</CardTitle>
                    <CardDescription>
                      {product.category} • {product.type} • ${product.price}
                    </CardDescription>
                  </div>
                  <Badge className={product.active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                    {product.active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600 mb-4">{product.description}</p>
                <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                  <div>
                    <span className="font-medium">SKU:</span> {product.sku}
                  </div>
                  <div>
                    <span className="font-medium">Sort:</span> {product.sort_order}
                  </div>
                  <div>
                    <span className="font-medium">Mode:</span> {product.checkout_mode}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEditProduct(product)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeleteProduct(product.id)}
                  >
                    Deactivate
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {products.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-500">No products yet. Add your first product to get started.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Note: AdminSidebar and AdminLayout are now imported from @/components/layout/AdminLayout

// Admin Login Page
const AdminPortalLoginPage = () => {
  const { adminUser, adminLogin } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (adminUser) {
      navigate('/admin/portal/products');
    }
  }, [adminUser, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await adminLogin(email, password);
    if (result.success) {
      navigate('/admin/portal/products');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-emerald-100 rounded-full">
              <Sparkles className="h-8 w-8 text-emerald-600" />
            </div>
          </div>
          <CardTitle className="text-2xl">Admin Portal</CardTitle>
          <CardDescription>Sign in to manage your store</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                {error}
              </div>
            )}
            <div>
              <Label htmlFor="admin-email">Email</Label>
              <Input
                id="admin-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@pepfactor.com"
                required
              />
            </div>
            <div>
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Sign In
            </Button>
          </form>
          <div className="mt-4 text-center">
            <a
              href="/admin/portal/forgot-password"
              className="text-sm text-emerald-600 hover:text-emerald-700 hover:underline"
            >
              Forgot password?
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Forgot Password Page
const AdminForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/admin/password/forgot`, { email });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-emerald-100 rounded-full">
                <Check className="h-8 w-8 text-emerald-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">Check Your Email</CardTitle>
            <CardDescription>
              If an admin account exists with that email, we've sent password reset instructions.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 text-center mb-4">
              The link will expire in 30 minutes.
            </p>
            <Button
              onClick={() => navigate('/admin/portal/login')}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-emerald-100 rounded-full">
              <Mail className="h-8 w-8 text-emerald-600" />
            </div>
          </div>
          <CardTitle className="text-2xl">Forgot Password</CardTitle>
          <CardDescription>Enter your email to receive a password reset link</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                {error}
              </div>
            )}
            <div>
              <Label htmlFor="reset-email">Email</Label>
              <Input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@pepfactor.com"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Send Reset Link
            </Button>
          </form>
          <div className="mt-4 text-center">
            <a
              href="/admin/portal/login"
              className="text-sm text-slate-500 hover:text-slate-700 hover:underline"
            >
              Back to login
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Reset Password Page
const AdminResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 12) {
      setError('Password must be at least 12 characters');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/admin/password/reset`, { 
        token, 
        newPassword: password 
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">Invalid Link</CardTitle>
            <CardDescription>
              This password reset link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/admin/portal/forgot-password')}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              Request New Link
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-emerald-100 rounded-full">
                <Check className="h-8 w-8 text-emerald-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">Password Reset!</CardTitle>
            <CardDescription>
              Your password has been successfully reset.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/admin/portal/login')}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              Login with New Password
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-emerald-100 rounded-full">
              <Sparkles className="h-8 w-8 text-emerald-600" />
            </div>
          </div>
          <CardTitle className="text-2xl">Reset Password</CardTitle>
          <CardDescription>Enter your new password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                {error}
              </div>
            )}
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                required
                minLength={12}
              />
              <p className="text-xs text-slate-500 mt-1">Minimum 12 characters</p>
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••••••"
                required
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Reset Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Products Page (New with Visibility Controls)
const AdminPortalProductsPage = () => {
  const { adminToken, adminUser } = useAdminAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/admin/products?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [statusFilter, categoryFilter]);

  const handleSearch = () => {
    fetchProducts();
  };

  const handleVisibilityToggle = async (product, field) => {
    // Check if product is not ACTIVE
    const currentStatus = product.status || 'DRAFT';
    if (currentStatus !== 'ACTIVE') {
      toast.error(`Set status to ACTIVE first to publish this product.`);
      return;
    }

    // If turning off both DTC and Partner while ACTIVE, show confirm
    const newValue = !product[field];
    const otherField = field === 'isVisibleDTC' ? 'isVisiblePartner' : 'isVisibleDTC';
    if (!newValue && !product[otherField]) {
      setConfirmModal({
        type: 'hideFromAll',
        product,
        field,
        message: 'This will hide the product from both channels. Continue?'
      });
      return;
    }

    await toggleVisibility(product.id, { [field]: newValue });
  };

  const toggleVisibility = async (productId, visibility) => {
    try {
      await axios.patch(`${API}/admin/products/${productId}/visibility`, visibility, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      toast.success('Visibility updated');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update visibility');
    }
    setConfirmModal(null);
  };

  const handleStatusChange = async (product, newStatus) => {
    try {
      await axios.patch(`${API}/admin/products/${product.id}/status`, { status: newStatus }, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      toast.success(`Status changed to ${newStatus}`);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, productData, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/admin/products`, productData, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        toast.success('Product created');
      }
      setShowForm(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      DRAFT: 'bg-slate-100 text-slate-700',
      ACTIVE: 'bg-emerald-100 text-emerald-700',
      ARCHIVED: 'bg-red-100 text-red-700'
    };
    const icons = {
      DRAFT: <Edit className="h-3 w-3 mr-1" />,
      ACTIVE: <CheckCircle className="h-3 w-3 mr-1" />,
      ARCHIVED: <Archive className="h-3 w-3 mr-1" />
    };
    return (
      <Badge className={`flex items-center ${styles[status] || styles.DRAFT}`}>
        {icons[status]}
        {status || 'DRAFT'}
      </Badge>
    );
  };

  return (
    <AdminLayout currentPage="products">
      <div className="p-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Products</h1>
            <p className="text-slate-600 mt-1">Manage product catalog and visibility</p>
          </div>
          <Button
            onClick={() => { setEditingProduct(null); setShowForm(true); }}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Product
          </Button>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-sm text-slate-600 mb-1 block">Search</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Search by name, SKU..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button variant="outline" onClick={handleSearch}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="w-40">
            <Label className="text-sm text-slate-600 mb-1 block">Status</Label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">All Status</option>
              <option value="DRAFT">Draft</option>
              <option value="ACTIVE">Active</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>
          <div className="w-40">
            <Label className="text-sm text-slate-600 mb-1 block">Category</Label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">All Categories</option>
              <option value="consumer">Consumer</option>
              <option value="provider">Provider</option>
            </select>
          </div>
          <Button variant="outline" onClick={() => { setSearchTerm(''); setStatusFilter(''); setCategoryFilter(''); fetchProducts(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
        </div>

        {/* Product Form Modal */}
        {showForm && (
          <AdminProductForm
            product={editingProduct}
            onSave={handleSaveProduct}
            onCancel={() => { setShowForm(false); setEditingProduct(null); }}
          />
        )}

        {/* Products Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-slate-600">Product</th>
                  <th className="text-left p-4 font-medium text-slate-600">SKU</th>
                  <th className="text-left p-4 font-medium text-slate-600">Price</th>
                  <th className="text-left p-4 font-medium text-slate-600">Status</th>
                  <th className="text-center p-4 font-medium text-slate-600">DTC</th>
                  <th className="text-center p-4 font-medium text-slate-600">Partner</th>
                  <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {products.map(product => (
                  <tr key={product.id} className="hover:bg-slate-50">
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-slate-900">{product.name}</p>
                        <p className="text-sm text-slate-500">{product.category} • {product.product_type}</p>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600">{product.sku}</td>
                    <td className="p-4 text-slate-900 font-medium">${product.price?.toFixed(2)}</td>
                    <td className="p-4">
                      <select
                        value={product.status || 'DRAFT'}
                        onChange={(e) => handleStatusChange(product, e.target.value)}
                        className={`px-2 py-1 rounded text-sm border-0 cursor-pointer ${
                          product.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700' :
                          product.status === 'ARCHIVED' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }`}
                      >
                        <option value="DRAFT">Draft</option>
                        <option value="ACTIVE">Active</option>
                        <option value="ARCHIVED">Archived</option>
                      </select>
                    </td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => handleVisibilityToggle(product, 'isVisibleDTC')}
                        disabled={(product.status || 'DRAFT') !== 'ACTIVE'}
                        className={`p-2 rounded-lg transition-colors ${
                          product.isVisibleDTC
                            ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                            : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                        } ${(product.status || 'DRAFT') !== 'ACTIVE' ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title={product.isVisibleDTC ? 'Visible on DTC Store' : 'Hidden from DTC Store'}
                      >
                        {product.isVisibleDTC ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => handleVisibilityToggle(product, 'isVisiblePartner')}
                        disabled={(product.status || 'DRAFT') !== 'ACTIVE'}
                        className={`p-2 rounded-lg transition-colors ${
                          product.isVisiblePartner
                            ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                            : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                        } ${(product.status || 'DRAFT') !== 'ACTIVE' ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title={product.isVisiblePartner ? 'Visible on Partner Portal' : 'Hidden from Partner Portal'}
                      >
                        {product.isVisiblePartner ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="p-4 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setEditingProduct(product); setShowForm(true); }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {products.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No products found. Try adjusting your filters.
              </div>
            )}
          </div>
        )}

        {/* Summary Stats */}
        <div className="mt-6 grid grid-cols-4 gap-4">
          <Card className="p-4">
            <p className="text-sm text-slate-500">Total Products</p>
            <p className="text-2xl font-bold">{products.length}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-slate-500">Active</p>
            <p className="text-2xl font-bold text-emerald-600">
              {products.filter(p => p.status === 'ACTIVE').length}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-slate-500">Visible DTC</p>
            <p className="text-2xl font-bold text-emerald-600">
              {products.filter(p => p.isVisibleDTC).length}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-slate-500">Visible Partner</p>
            <p className="text-2xl font-bold text-blue-600">
              {products.filter(p => p.isVisiblePartner).length}
            </p>
          </Card>
        </div>

        {/* Confirm Modal */}
        {confirmModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md m-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Confirm Action
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-6">{confirmModal.message}</p>
                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1" onClick={() => setConfirmModal(null)}>
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-amber-600 hover:bg-amber-700"
                    onClick={() => toggleVisibility(confirmModal.product.id, { [confirmModal.field]: false })}
                  >
                    Yes, Hide
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// Admin Product Form (Enhanced)
// Admin Product Form - Enhanced with PHASE 2 fields
const AdminProductForm = ({ product, onSave, onCancel }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    // Basic info
    name: product?.name || '',
    description: product?.description || '',
    category: product?.category || 'consumer',
    type: product?.type || 'one_time',
    product_type: product?.product_type || 'CONSUMABLE',
    sku: product?.sku || '',
    image_url: product?.image_url || '',
    active: product?.active ?? true,
    sort_order: product?.sort_order || 0,
    
    // Legacy pricing (for backward compatibility)
    price: product?.price || 0,
    priceDTC: product?.priceDTC || null,
    pricePartner: product?.pricePartner || null,
    currency: product?.currency || 'USD',
    
    // Structured pricing (PHASE 2)
    pricing: product?.pricing || {
      dtc: { price: product?.priceDTC || product?.price || 0, compareAtPrice: null },
      partner: { wholesalePrice: product?.pricePartner || 0, msrp: null, mapPrice: null }
    },
    
    // Pack/Order quantities (PHASE 2)
    casePackQty: product?.casePackQty || 1,
    minOrderQtyDTC: product?.minOrderQtyDTC || 1,
    minOrderQtyPartner: product?.minOrderQtyPartner || 1,
    
    // Checkout config
    checkout_mode: product?.checkout_mode || 'demo',
    payment_link_url: product?.payment_link_url || '',
    stripe_price_id: product?.stripe_price_id || '',
    
    // Status & Visibility
    status: product?.status || 'DRAFT',
    isVisibleDTC: product?.isVisibleDTC || false,
    isVisiblePartner: product?.isVisiblePartner || false,
    
    // Claims (PHASE 2)
    claims: product?.claims || {
      publicClaimsByMarket: { US: [], AU: [], NZ: [] },
      warningsAndContraindications: { US: [], AU: [], NZ: [] }
    },
    
    // Regulatory (PHASE 2)
    regulatory: product?.regulatory || {
      fdaStatus: 'NONE',
      fdaKNumber: '',
      intendedUseText: ''
    },
    
    // Support (PHASE 2)
    support: product?.support || {
      supportResponsibility: 'PEPFACTOR',
      warrantyPolicyUrl: '',
      returnsPolicyUrl: '',
      trainingRequired: false,
      trainingAssets: []
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.checkout_mode === 'stripe_payment_link' && !formData.payment_link_url) {
      toast.error('Payment link URL required');
      return;
    }
    if (formData.checkout_mode === 'stripe_price_id' && !formData.stripe_price_id) {
      toast.error('Stripe price ID required');
      return;
    }
    
    // Sync structured pricing with legacy fields
    const dataToSave = {
      ...formData,
      priceDTC: formData.pricing?.dtc?.price || formData.priceDTC,
      pricePartner: formData.pricing?.partner?.wholesalePrice || formData.pricePartner
    };
    
    onSave(dataToSave);
  };

  const updatePricing = (channel, field, value) => {
    setFormData(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [channel]: {
          ...prev.pricing[channel],
          [field]: value
        }
      }
    }));
  };

  const updateRegulatory = (field, value) => {
    setFormData(prev => ({
      ...prev,
      regulatory: {
        ...prev.regulatory,
        [field]: value
      }
    }));
  };

  const updateSupport = (field, value) => {
    setFormData(prev => ({
      ...prev,
      support: {
        ...prev.support,
        [field]: value
      }
    }));
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'pricing', label: 'Pricing & Qty' },
    { id: 'visibility', label: 'Status & Visibility' },
    { id: 'checkout', label: 'Checkout' },
    { id: 'regulatory', label: 'Regulatory' },
    { id: 'support', label: 'Support' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-auto py-8">
      <Card className="w-full max-w-4xl m-4 max-h-[90vh] overflow-auto">
        <CardHeader className="flex flex-row items-center justify-between border-b">
          <CardTitle>{product ? 'Edit Product' : 'New Product'}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        
        {/* Tabs */}
        <div className="border-b px-6">
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-emerald-600 text-emerald-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            
            {/* Basic Info Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Product Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label>SKU *</Label>
                    <Input
                      value={formData.sku}
                      onChange={(e) => setFormData({...formData, sku: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>Description *</Label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full min-h-[100px] px-3 py-2 border rounded-md"
                    required
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Category</Label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
                    >
                      <option value="consumer">Consumer</option>
                      <option value="provider">Provider</option>
                    </select>
                  </div>
                  <div>
                    <Label>Purchase Type</Label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value})}
                      className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
                    >
                      <option value="one_time">One Time</option>
                      <option value="subscription">Subscription</option>
                    </select>
                  </div>
                  <div>
                    <Label>Product Type</Label>
                    <select
                      value={formData.product_type}
                      onChange={(e) => setFormData({...formData, product_type: e.target.value})}
                      className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
                    >
                      <option value="CONSUMABLE">Consumable</option>
                      <option value="HOME_DEVICE">Home Device</option>
                      <option value="PRO_DEVICE">Pro Device</option>
                      <option value="SERVICE">Service</option>
                      <option value="SUBSCRIPTION">Subscription</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Image URL</Label>
                    <Input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <Label>Sort Order</Label>
                    <Input
                      type="number"
                      value={formData.sort_order}
                      onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Pricing & Qty Tab */}
            {activeTab === 'pricing' && (
              <div className="space-y-6">
                {/* Base Price */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-3">Base Price</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Base Price *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Currency</Label>
                      <select
                        value={formData.currency}
                        onChange={(e) => setFormData({...formData, currency: e.target.value})}
                        className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
                      >
                        <option value="USD">USD</option>
                        <option value="AUD">AUD</option>
                        <option value="NZD">NZD</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* DTC Pricing */}
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <h4 className="font-medium mb-3 text-emerald-800">DTC Channel Pricing</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>DTC Price</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.pricing?.dtc?.price || ''}
                        onChange={(e) => updatePricing('dtc', 'price', parseFloat(e.target.value) || 0)}
                        placeholder="Same as base"
                      />
                    </div>
                    <div>
                      <Label>Compare At (Strikethrough)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.pricing?.dtc?.compareAtPrice || ''}
                        onChange={(e) => updatePricing('dtc', 'compareAtPrice', e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                </div>

                {/* Partner Pricing */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium mb-3 text-blue-800">Partner Channel Pricing</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Wholesale Price</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.pricing?.partner?.wholesalePrice || ''}
                        onChange={(e) => updatePricing('partner', 'wholesalePrice', parseFloat(e.target.value) || 0)}
                        placeholder="Partner cost"
                      />
                    </div>
                    <div>
                      <Label>MSRP</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.pricing?.partner?.msrp || ''}
                        onChange={(e) => updatePricing('partner', 'msrp', e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="Suggested retail"
                      />
                    </div>
                    <div>
                      <Label>MAP Price</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.pricing?.partner?.mapPrice || ''}
                        onChange={(e) => updatePricing('partner', 'mapPrice', e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="Min advertised"
                      />
                    </div>
                  </div>
                </div>

                {/* Pack/Order Quantities */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-3">Pack & Order Quantities</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Case Pack Qty</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.casePackQty}
                        onChange={(e) => setFormData({...formData, casePackQty: parseInt(e.target.value) || 1})}
                      />
                      <p className="text-xs text-slate-500 mt-1">Units per case (6 for kits)</p>
                    </div>
                    <div>
                      <Label>Min Order Qty (DTC)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.minOrderQtyDTC}
                        onChange={(e) => setFormData({...formData, minOrderQtyDTC: parseInt(e.target.value) || 1})}
                      />
                    </div>
                    <div>
                      <Label>Min Order Qty (Partner)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.minOrderQtyPartner}
                        onChange={(e) => setFormData({...formData, minOrderQtyPartner: parseInt(e.target.value) || 1})}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Status & Visibility Tab */}
            {activeTab === 'visibility' && (
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium mb-3">Status</h4>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    <option value="DRAFT">Draft</option>
                    <option value="ACTIVE">Active</option>
                    <option value="ARCHIVED">Archived</option>
                  </select>
                  {formData.status !== 'ACTIVE' && (
                    <p className="text-sm text-amber-600 flex items-center gap-1 mt-2">
                      <AlertTriangle className="h-3 w-3" />
                      Set status to ACTIVE to enable visibility toggles
                    </p>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className={`p-4 rounded-lg ${formData.isVisibleDTC ? 'bg-emerald-50 border-2 border-emerald-200' : 'bg-slate-50'}`}>
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="visibleDTC"
                        checked={formData.isVisibleDTC}
                        onCheckedChange={(checked) => setFormData({...formData, isVisibleDTC: checked})}
                        disabled={formData.status !== 'ACTIVE'}
                      />
                      <div>
                        <Label htmlFor="visibleDTC" className={formData.status !== 'ACTIVE' ? 'text-slate-400' : ''}>
                          Show on DTC Store
                        </Label>
                        <p className="text-xs text-slate-500">Visible to public consumers</p>
                      </div>
                    </div>
                  </div>
                  <div className={`p-4 rounded-lg ${formData.isVisiblePartner ? 'bg-blue-50 border-2 border-blue-200' : 'bg-slate-50'}`}>
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="visiblePartner"
                        checked={formData.isVisiblePartner}
                        onCheckedChange={(checked) => setFormData({...formData, isVisiblePartner: checked})}
                        disabled={formData.status !== 'ACTIVE'}
                      />
                      <div>
                        <Label htmlFor="visiblePartner" className={formData.status !== 'ACTIVE' ? 'text-slate-400' : ''}>
                          Show on Partner Portal
                        </Label>
                        <p className="text-xs text-slate-500">Visible to approved partners</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Checkout Tab */}
            {activeTab === 'checkout' && (
              <div className="space-y-4">
                <div>
                  <Label>Checkout Mode</Label>
                  <select
                    value={formData.checkout_mode}
                    onChange={(e) => setFormData({...formData, checkout_mode: e.target.value})}
                    className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    <option value="demo">Demo (No payment)</option>
                    <option value="stripe_payment_link">Stripe Payment Link</option>
                    <option value="stripe_price_id">Stripe Price ID</option>
                    <option value="enquiry_only">Enquiry Only</option>
                  </select>
                </div>
                
                {formData.checkout_mode === 'stripe_payment_link' && (
                  <div>
                    <Label>Payment Link URL *</Label>
                    <Input
                      type="url"
                      value={formData.payment_link_url}
                      onChange={(e) => setFormData({...formData, payment_link_url: e.target.value})}
                      placeholder="https://buy.stripe.com/..."
                    />
                  </div>
                )}
                
                {formData.checkout_mode === 'stripe_price_id' && (
                  <div>
                    <Label>Stripe Price ID *</Label>
                    <Input
                      value={formData.stripe_price_id}
                      onChange={(e) => setFormData({...formData, stripe_price_id: e.target.value})}
                      placeholder="price_..."
                    />
                  </div>
                )}
              </div>
            )}

            {/* Regulatory Tab */}
            {activeTab === 'regulatory' && (
              <div className="space-y-4">
                <p className="text-sm text-slate-500">
                  Regulatory fields are primarily for HOME_DEVICE and PRO_DEVICE products.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>FDA Status</Label>
                    <select
                      value={formData.regulatory?.fdaStatus || 'NONE'}
                      onChange={(e) => updateRegulatory('fdaStatus', e.target.value)}
                      className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
                    >
                      <option value="NONE">None</option>
                      <option value="510K_CLEARED">510(k) Cleared</option>
                      <option value="DE_NOVO">De Novo</option>
                      <option value="PMA_APPROVED">PMA Approved</option>
                    </select>
                  </div>
                  <div>
                    <Label>FDA K Number</Label>
                    <Input
                      value={formData.regulatory?.fdaKNumber || ''}
                      onChange={(e) => updateRegulatory('fdaKNumber', e.target.value)}
                      placeholder="K123456"
                    />
                  </div>
                </div>
                <div>
                  <Label>Intended Use Text</Label>
                  <textarea
                    value={formData.regulatory?.intendedUseText || ''}
                    onChange={(e) => updateRegulatory('intendedUseText', e.target.value)}
                    className="w-full min-h-[100px] px-3 py-2 border rounded-md"
                    placeholder="For personal wellness use only..."
                  />
                </div>
              </div>
            )}

            {/* Support Tab */}
            {activeTab === 'support' && (
              <div className="space-y-4">
                <div>
                  <Label>Support Responsibility</Label>
                  <select
                    value={formData.support?.supportResponsibility || 'PEPFACTOR'}
                    onChange={(e) => updateSupport('supportResponsibility', e.target.value)}
                    className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
                  >
                    <option value="PEPFACTOR">PepFactor</option>
                    <option value="PARTNER">Partner</option>
                    <option value="MANUFACTURER">Manufacturer</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Warranty Policy URL</Label>
                    <Input
                      type="url"
                      value={formData.support?.warrantyPolicyUrl || ''}
                      onChange={(e) => updateSupport('warrantyPolicyUrl', e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <Label>Returns Policy URL</Label>
                    <Input
                      type="url"
                      value={formData.support?.returnsPolicyUrl || ''}
                      onChange={(e) => updateSupport('returnsPolicyUrl', e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                  <Checkbox
                    id="trainingRequired"
                    checked={formData.support?.trainingRequired || false}
                    onCheckedChange={(checked) => updateSupport('trainingRequired', checked)}
                  />
                  <div>
                    <Label htmlFor="trainingRequired">Training Required</Label>
                    <p className="text-xs text-slate-500">Partners must complete training before selling</p>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {product ? 'Update Product' : 'Create Product'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

const ProductForm = ({ product, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    description: product?.description || '',
    category: product?.category || 'consumer',
    type: product?.type || 'one_time',
    price: product?.price || 0,
    currency: product?.currency || 'USD',
    sku: product?.sku || '',
    active: product?.active ?? true,
    sort_order: product?.sort_order || 0,
    checkout_mode: product?.checkout_mode || 'demo',
    payment_link_url: product?.payment_link_url || '',
    stripe_price_id: product?.stripe_price_id || '',
    image_url: product?.image_url || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.checkout_mode === 'stripe_payment_link' && !formData.payment_link_url) {
      toast.error('Payment link URL required for Stripe payment link mode');
      return;
    }
    if (formData.checkout_mode === 'stripe_price_id' && !formData.stripe_price_id) {
      toast.error('Stripe price ID required for Stripe price ID mode');
      return;
    }
    
    onSave(formData);
  };

  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle>{product ? 'Edit Product' : 'New Product'}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Product Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="sku">SKU *</Label>
              <Input
                id="sku"
                value={formData.sku}
                onChange={(e) => setFormData({...formData, sku: e.target.value})}
                required
              />
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description *</Label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full min-h-[100px] px-3 py-2 border border-slate-300 rounded-md"
              required
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="category">Category *</Label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                required
              >
                <option value="consumer">Consumer</option>
                <option value="provider">Provider</option>
              </select>
            </div>

            <div>
              <Label htmlFor="type">Type *</Label>
              <select
                id="type"
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                required
              >
                <option value="one_time">One Time</option>
                <option value="subscription">Subscription</option>
              </select>
            </div>

            <div>
              <Label htmlFor="price">Price *</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value)})}
                required
              />
            </div>

            <div>
              <Label htmlFor="sort_order">Sort Order</Label>
              <Input
                id="sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value)})}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="checkout_mode">Checkout Mode *</Label>
            <select
              id="checkout_mode"
              value={formData.checkout_mode}
              onChange={(e) => setFormData({...formData, checkout_mode: e.target.value})}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              required
            >
              <option value="demo">Demo</option>
              <option value="stripe_payment_link">Stripe Payment Link</option>
              <option value="stripe_price_id">Stripe Price ID</option>
            </select>
          </div>

          {formData.checkout_mode === 'stripe_payment_link' && (
            <div>
              <Label htmlFor="payment_link_url">Payment Link URL *</Label>
              <Input
                id="payment_link_url"
                type="url"
                value={formData.payment_link_url}
                onChange={(e) => setFormData({...formData, payment_link_url: e.target.value})}
                placeholder="https://buy.stripe.com/..."
              />
            </div>
          )}

          {formData.checkout_mode === 'stripe_price_id' && (
            <div>
              <Label htmlFor="stripe_price_id">Stripe Price ID *</Label>
              <Input
                id="stripe_price_id"
                value={formData.stripe_price_id}
                onChange={(e) => setFormData({...formData, stripe_price_id: e.target.value})}
                placeholder="price_..."
              />
            </div>
          )}

          <div>
            <Label htmlFor="image_url">Image URL</Label>
            <Input
              id="image_url"
              type="url"
              value={formData.image_url}
              onChange={(e) => setFormData({...formData, image_url: e.target.value})}
              placeholder="https://..."
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="active"
              checked={formData.active}
              onCheckedChange={(checked) => setFormData({...formData, active: checked})}
            />
            <Label htmlFor="active" className="cursor-pointer">Active</Label>
          </div>

          <div className="flex gap-4">
            <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
              Save Product
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ==================== SUBSCRIPTIONS PAGE ====================

const SubscriptionsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?category=consumer&active_only=true`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (product) => {
    try {
      if (product.checkout_mode === 'demo') {
        toast.info('Demo mode: Purchase simulated successfully!');
        return;
      }
      
      if (product.checkout_mode === 'stripe_payment_link' && product.payment_link_url) {
        window.location.href = product.payment_link_url;
        return;
      }
      
      if (product.checkout_mode === 'stripe_price_id' && product.stripe_price_id) {
        // Create Stripe checkout session with price ID
        toast.info('Redirecting to Stripe checkout...');
        // In production, call backend to create checkout session
        return;
      }
      
      toast.error('Checkout not configured for this product');
    } catch (error) {
      toast.error('Purchase failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-12 text-center">
          <h1 className="text-4xl font-bold mb-2" data-testid="subscriptions-title">PepFactor Products</h1>
          <p className="text-slate-600" data-testid="subscriptions-description">
            Science-backed treatments and supplements
          </p>
        </div>

        {products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-500">No products available at this time.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {products.map(product => (
              <Card key={product.id} className="border-2 hover:border-emerald-400 transition-all">
                <CardHeader>
                  {product.image_url && (
                    <img src={product.image_url} alt={product.name} className="w-full h-48 object-cover rounded-lg mb-4" />
                  )}
                  <CardTitle className="text-xl" data-testid={`product-name-${product.sku}`}>{product.name}</CardTitle>
                  <div className="text-3xl font-bold text-emerald-600" data-testid={`product-price-${product.sku}`}>
                    ${product.price.toFixed(2)}
                  </div>
                  <Badge className="w-fit">{product.type === 'subscription' ? 'Subscription' : 'One-Time Purchase'}</Badge>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 mb-6">{product.description}</p>
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handlePurchase(product)}
                    data-testid={`subscribe-btn-${product.sku}`}
                  >
                    {product.type === 'subscription' ? 'Subscribe Now' : 'Purchase Now'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== CLINIC SIGNUP PAGE ====================

const ClinicSignupPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    mobile: '',
    position: 'staff',
    lat: 0,
    lng: 0,
    monthly_kit_commitment: 12
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.monthly_kit_commitment < 12) {
      toast.error('Minimum 12 kits per month required');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/clinics/register`, formData);
      toast.success('Clinic registration submitted! Awaiting admin approval.');
      navigate('/account');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">PepFactor USA</h1>
          <h2 className="text-2xl font-semibold mb-2">Register Your Clinic</h2>
          <p className="text-slate-600">Join the PepFactor network (minimum 12 kits/month)</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Clinic Information</CardTitle>
            <CardDescription>
              All fields required. Minimum commitment: 12 kits per month.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Clinic Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>

              <div>
                <Label htmlFor="address">Street Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    required
                    placeholder="CA"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="zip_code">ZIP Code *</Label>
                  <Input
                    id="zip_code"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="mobile">Mobile Number *</Label>
                  <Input
                    id="mobile"
                    value={formData.mobile}
                    onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                    required
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <Label htmlFor="position">Position *</Label>
                  <select
                    id="position"
                    value={formData.position}
                    onChange={(e) => setFormData({...formData, position: e.target.value})}
                    required
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="staff">Staff</option>
                    <option value="manager">Manager</option>
                    <option value="owner">Owner</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="monthly_kit_commitment">Monthly Kit Commitment *</Label>
                <Input
                  id="monthly_kit_commitment"
                  type="number"
                  min="12"
                  value={formData.monthly_kit_commitment}
                  onChange={(e) => setFormData({...formData, monthly_kit_commitment: parseInt(e.target.value)})}
                  required
                />
                <p className="text-xs text-slate-500 mt-1">Minimum: 12 kits per month</p>
              </div>

              <div className="pt-4 border-t">
                <div className="bg-emerald-50 p-4 rounded-lg mb-4">
                  <h3 className="font-semibold text-emerald-900 mb-2">What You Get:</h3>
                  <ul className="text-sm text-emerald-800 space-y-1">
                    <li>✓ Access to PepFactor AI capture technology</li>
                    <li>✓ Monthly kit delivery (minimum 12 kits)</li>
                    <li>✓ Online booking integration via Cal.com</li>
                    <li>✓ Patient progress tracking dashboard</li>
                    <li>✓ Marketing materials and support</li>
                  </ul>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit for Approval'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== ADMIN DASHBOARD ====================

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [pendingClinics, setPendingClinics] = useState([]);
  const [allClinics, setAllClinics] = useState([]);
  const [providerApplications, setProviderApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending'); // pending, all, create, providers

  React.useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'pending') {
        const response = await axios.get(`${API}/admin/clinics/pending`);
        setPendingClinics(response.data);
      } else if (activeTab === 'all') {
        const response = await axios.get(`${API}/admin/clinics`);
        setAllClinics(response.data);
      } else if (activeTab === 'providers') {
        const response = await axios.get(`${API}/admin/provider-applications`);
        setProviderApplications(response.data);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (clinicId, action) => {
    try {
      await axios.patch(`${API}/admin/clinics/${clinicId}/approve`, { action });
      toast.success(`Clinic ${action}d successfully`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} clinic`);
    }
  };

  const handleCreateClinic = async (formData) => {
    try {
      await axios.post(`${API}/admin/clinics`, formData);
      toast.success('Clinic created successfully');
      setActiveTab('all');
    } catch (error) {
      toast.error('Failed to create clinic');
    }
  };

  const handleProviderApproval = async (applicationId, action) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = action === 'approve' 
        ? `/admin/provider-applications/${applicationId}/approve`
        : `/admin/provider-applications/${applicationId}/reject`;
      
      await axios.post(`${API}${endpoint}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success(`Provider application ${action}d successfully`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${action} provider application`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Admin Dashboard</h1>
          <p className="text-slate-600">Manage clinic registrations and approvals</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b">
          <button
            onClick={() => setActiveTab('pending')}
            className={`pb-3 px-4 font-medium ${activeTab === 'pending' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-slate-600'}`}
          >
            Pending Approvals ({pendingClinics.length})
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`pb-3 px-4 font-medium ${activeTab === 'all' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-slate-600'}`}
          >
            All Clinics
          </button>
          <button
            onClick={() => setActiveTab('create')}
            className={`pb-3 px-4 font-medium ${activeTab === 'create' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-slate-600'}`}
          >
            Create Clinic
          </button>
          <button
            onClick={() => setActiveTab('providers')}
            className={`pb-3 px-4 font-medium ${activeTab === 'providers' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-slate-600'}`}
          >
            Provider Applications ({providerApplications.length})
          </button>
          <button
            onClick={() => navigate('/admin/products')}
            className="pb-3 px-4 font-medium text-slate-600 hover:text-emerald-600"
          >
            Products
          </button>
        </div>

        {/* Pending Clinics */}
        {activeTab === 'pending' && (
          <div className="space-y-4">
            {pendingClinics.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-slate-500">No pending clinic registrations</p>
                </CardContent>
              </Card>
            ) : (
              pendingClinics.map(clinic => (
                <Card key={clinic.id} className="border-2 border-amber-200">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle>{clinic.name}</CardTitle>
                        <CardDescription>
                          {clinic.address}, {clinic.city}, {clinic.state} {clinic.zip_code}
                        </CardDescription>
                      </div>
                      <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm font-medium">Monthly Kit Commitment</p>
                        <p className="text-2xl font-bold text-emerald-600">{clinic.monthly_kit_commitment} kits</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Submitted</p>
                        <p className="text-sm text-slate-600">{new Date(clinic.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => handleApproval(clinic.id, 'approve')}
                      >
                        <Check className="mr-2 h-4 w-4" /> Approve
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleApproval(clinic.id, 'reject')}
                      >
                        Reject
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* All Clinics */}
        {activeTab === 'all' && (
          <div className="space-y-4">
            {allClinics.map(clinic => (
              <Card key={clinic.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle>{clinic.name}</CardTitle>
                      <CardDescription>
                        {clinic.address}, {clinic.city}, {clinic.state}
                      </CardDescription>
                    </div>
                    <Badge className={
                      clinic.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                      clinic.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-700'
                    }>
                      {clinic.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="font-medium">Kit Commitment</p>
                      <p className="text-slate-600">{clinic.monthly_kit_commitment} /month</p>
                    </div>
                    <div>
                      <p className="font-medium">Payment Status</p>
                      <p className="text-slate-600">{clinic.payment_status}</p>
                    </div>
                    <div>
                      <p className="font-medium">Created</p>
                      <p className="text-slate-600">{new Date(clinic.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Clinic */}
        {activeTab === 'create' && (
          <AdminCreateClinic onSubmit={handleCreateClinic} />
        )}

        {/* Provider Applications */}
        {activeTab === 'providers' && (
          <div className="space-y-4">
            {providerApplications.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-slate-500">No provider applications</p>
                </CardContent>
              </Card>
            ) : (
              providerApplications.map(application => (
                <Card key={application.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle>{application.clinic_name}</CardTitle>
                        <CardDescription>
                          {application.owner_name} • {application.email}
                        </CardDescription>
                      </div>
                      <Badge className={
                        application.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                        application.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-red-100 text-red-700'
                      }>
                        {application.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-sm font-medium">Location</p>
                        <p className="text-sm text-slate-600">
                          {application.street_address}<br />
                          {application.city}, {application.state} {application.zip_code}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Contact</p>
                        <p className="text-sm text-slate-600">
                          {application.phone}<br />
                          {application.email}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium">Monthly Volume</p>
                        <p className="text-sm text-slate-600">{application.monthly_volume}</p>
                      </div>
                    </div>
                    <div className="mb-4">
                      <p className="text-sm font-medium mb-1">Services</p>
                      <p className="text-sm text-slate-600">{application.services?.join(', ')}</p>
                    </div>
                    {application.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => handleProviderApproval(application.id, 'approve')}
                        >
                          <Check className="mr-2 h-4 w-4" /> Approve
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleProviderApproval(application.id, 'reject')}
                        >
                          <X className="mr-2 h-4 w-4" /> Reject
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const AdminCreateClinic = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    lat: 0,
    lng: 0,
    monthly_kit_commitment: 0,
    payment_status: 'active'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create New Clinic (Admin)</CardTitle>
        <CardDescription>No minimum kit commitment required for admin-created clinics</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="admin_name">Clinic Name *</Label>
            <Input
              id="admin_name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
          </div>
          <div>
            <Label htmlFor="admin_address">Address *</Label>
            <Input
              id="admin_address"
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              required
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="admin_city">City *</Label>
              <Input
                id="admin_city"
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="admin_state">State *</Label>
              <Input
                id="admin_state"
                value={formData.state}
                onChange={(e) => setFormData({...formData, state: e.target.value})}
                required
              />
            </div>
            <div>
              <Label htmlFor="admin_zip">ZIP *</Label>
              <Input
                id="admin_zip"
                value={formData.zip_code}
                onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                required
              />
            </div>
          </div>
          <div>
            <Label htmlFor="admin_kit">Monthly Kit Commitment (optional)</Label>
            <Input
              id="admin_kit"
              type="number"
              min="0"
              value={formData.monthly_kit_commitment}
              onChange={(e) => setFormData({...formData, monthly_kit_commitment: parseInt(e.target.value)})}
            />
          </div>
          <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700">
            Create Clinic
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// ==================== PHOTO GALLERY PAGE ====================

const PhotoGalleryPage = () => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [viewMode, setViewMode] = useState('timeline'); // timeline or grid

  React.useEffect(() => {
    fetchPhotos();
  }, [viewMode]);

  const fetchPhotos = async () => {
    try {
      if (viewMode === 'timeline') {
        const response = await axios.get(`${API}/capture/timeline`);
        setTimeline(response.data);
      } else {
        const response = await axios.get(`${API}/capture/photos`);
        setPhotos(response.data);
      }
    } catch (error) {
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev => 
      prev.includes(photoId) 
        ? prev.filter(id => id !== photoId)
        : prev.length < 4 ? [...prev, photoId] : prev
    );
  };

  const handleCompare = () => {
    if (selectedPhotos.length < 2) {
      toast.error('Select at least 2 photos to compare');
      return;
    }
    navigate('/progress/compare', { state: { photoIds: selectedPhotos } });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Photo Gallery</h1>
            <p className="text-slate-600">Track your progress over time</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => navigate('/capture')} className="bg-emerald-600 hover:bg-emerald-700">
              <Camera className="mr-2 h-4 w-4" /> Take New Photo
            </Button>
            {selectedPhotos.length >= 2 && (
              <Button onClick={handleCompare} variant="outline">
                Compare ({selectedPhotos.length})
              </Button>
            )}
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={viewMode === 'timeline' ? 'default' : 'outline'}
            onClick={() => setViewMode('timeline')}
            size="sm"
          >
            Timeline View
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            onClick={() => setViewMode('grid')}
            size="sm"
          >
            Grid View
          </Button>
        </div>

        {viewMode === 'timeline' && timeline && (
          <div className="space-y-8">
            {Object.entries(timeline).map(([type, typePhotos]) => (
              typePhotos.length > 0 && (
                <div key={type}>
                  <h2 className="text-2xl font-bold mb-4 capitalize">{type.replace('_', ' ')}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {typePhotos.map(photo => (
                      <PhotoCard
                        key={photo.id}
                        photo={photo}
                        selected={selectedPhotos.includes(photo.id)}
                        onSelect={() => togglePhotoSelection(photo.id)}
                      />
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        )}

        {viewMode === 'grid' && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {photos.map(photo => (
              <PhotoCard
                key={photo.id}
                photo={photo}
                selected={selectedPhotos.includes(photo.id)}
                onSelect={() => togglePhotoSelection(photo.id)}
              />
            ))}
          </div>
        )}

        {((viewMode === 'grid' && photos.length === 0) || (viewMode === 'timeline' && timeline && Object.values(timeline).every(arr => arr.length === 0))) && (
          <Card>
            <CardContent className="p-12 text-center">
              <Camera className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 mb-4">No photos yet</p>
              <Button onClick={() => navigate('/capture')} className="bg-emerald-600 hover:bg-emerald-700">
                Take Your First Photo
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

const PhotoCard = ({ photo, selected, onSelect }) => {
  const [imageData, setImageData] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetchThumbnail();
  }, [photo.id]);

  const fetchThumbnail = async () => {
    try {
      const response = await axios.get(`${API}/capture/photos/${photo.id}?include_image=true`);
      setImageData(response.data.image_data);
    } catch (error) {
      console.error('Failed to load thumbnail:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={`cursor-pointer transition-all ${selected ? 'ring-2 ring-emerald-600' : ''}`}>
      <CardContent className="p-3">
        <div className="relative">
          <div className="aspect-square bg-slate-100 rounded-lg mb-2 flex items-center justify-center overflow-hidden">
            {loading ? (
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            ) : imageData ? (
              <img 
                src={imageData} 
                alt={photo.photo_label || photo.type}
                className="w-full h-full object-cover"
              />
            ) : (
              <Camera className="h-12 w-12 text-slate-300" />
            )}
          </div>
          <Checkbox
            checked={selected}
            onCheckedChange={onSelect}
            className="absolute top-2 right-2 bg-white"
          />
        </div>
        <p className="font-medium text-sm">{photo.photo_label || photo.type}</p>
        <p className="text-xs text-slate-600">{new Date(photo.captured_at).toLocaleDateString()}</p>
        {photo.ai_metrics && (
          <Badge className="mt-2 text-xs bg-emerald-100 text-emerald-700">
            AI Analyzed
          </Badge>
        )}
      </CardContent>
    </Card>
  );
};

// ==================== PROFESSIONAL ANALYSIS PAGE ====================

const ProfessionalAnalysisPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const photoData = location.state?.photoData;
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [treatmentPlan, setTreatmentPlan] = useState(null);
  const [showTreatments, setShowTreatments] = useState(false);

  React.useEffect(() => {
    if (photoData && photoData.image_base64) {
      runProfessionalAnalysis();
    }
  }, []);

  const runProfessionalAnalysis = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/capture/analyze-professional`, {
        image_base64: photoData.image_base64,
        body_area: photoData.body_area || 'face'
      });
      setAnalysis(response.data);
      toast.success('Professional analysis complete!');
    } catch (error) {
      toast.error('Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateTreatmentPlan = async () => {
    if (!analysis) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/capture/treatment-plan`, {
        analysis_id: analysis.meta?.analysis_id || 'temp',
        contraindications: []
      });
      setTreatmentPlan(response.data);
      setShowTreatments(true);
      toast.success('Treatment plan generated!');
    } catch (error) {
      toast.error('Failed to generate treatment plan');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mb-4" />
        <p className="text-lg font-medium">Running professional analysis...</p>
        <p className="text-sm text-slate-600">This may take 30-45 seconds</p>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-12 text-center">
            <p className="text-slate-500">No analysis data available</p>
            <Button onClick={() => navigate('/capture')} className="mt-4">
              Take New Photo
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const metrics = analysis.detailed_metrics || {};
  const primaryConditions = analysis.primary_conditions_detected || [];
  const fitzpatrick = analysis.fitzpatrick_skin_type || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <button
          onClick={() => navigate('/progress')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
        >
          <ArrowRight className="h-4 w-4 rotate-180" />
          Back to Gallery
        </button>

        <div className="mb-8 text-center">
          <Badge className="mb-4 bg-blue-100 text-blue-700">
            Professional Clinical Analysis
          </Badge>
          <h1 className="text-4xl font-bold mb-2">Skin Analysis Report</h1>
          <p className="text-slate-600">
            AI-powered clinical-grade assessment • {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Fitzpatrick Skin Type */}
        {fitzpatrick.estimated_type && (
          <Card className="mb-6 border-2 border-blue-200">
            <CardHeader>
              <CardTitle>Fitzpatrick Skin Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="text-5xl font-bold text-blue-600">
                  Type {fitzpatrick.estimated_type}
                </div>
                <div className="flex-1">
                  <p className="font-medium">{fitzpatrick.key_features || 'Skin type identified'}</p>
                  <p className="text-sm text-slate-600 mt-1">
                    Confidence: {fitzpatrick.confidence_level || 'High'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Primary Conditions */}
        {primaryConditions.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Primary Conditions Detected</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {primaryConditions.map((condition, idx) => (
                  <div key={idx} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold">{condition.condition_name || condition}</h3>
                      <Badge className={
                        (condition.severity_classification || '').includes('severe') ? 'bg-red-100 text-red-700' :
                        (condition.severity_classification || '').includes('moderate') ? 'bg-amber-100 text-amber-700' :
                        'bg-green-100 text-green-700'
                      }>
                        {condition.severity_classification || 'Mild'}
                      </Badge>
                    </div>
                    {condition.severity_score && (
                      <div className="mb-2">
                        <div className="flex justify-between text-sm mb-1">
                          <span>Severity</span>
                          <span>{condition.severity_score}/100</span>
                        </div>
                        <div className="w-full bg-slate-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full"
                            style={{width: `${condition.severity_score}%`}}
                          />
                        </div>
                      </div>
                    )}
                    {condition.affected_areas && (
                      <p className="text-sm text-slate-600">
                        Affected: {Array.isArray(condition.affected_areas) ? condition.affected_areas.join(', ') : condition.affected_areas}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Detailed Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Pigmentation */}
          {metrics.pigmentation && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Pigmentation</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(metrics.pigmentation).map(([key, value]) => (
                  <div key={key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="font-medium">{value}/100</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-emerald-600 h-2 rounded-full"
                        style={{width: `${value}%`}}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Texture */}
          {metrics.texture && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Texture</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(metrics.texture).map(([key, value]) => (
                  <div key={key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="font-medium">{value}/100</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-emerald-600 h-2 rounded-full"
                        style={{width: `${value}%`}}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Wrinkles & Aging */}
          {metrics.wrinkles_aging && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Wrinkles & Aging</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(metrics.wrinkles_aging).map(([key, value]) => (
                  <div key={key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="font-medium">{typeof value === 'number' ? `${value}/100` : value}</span>
                    </div>
                    {typeof value === 'number' && (
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div 
                          className="bg-emerald-600 h-2 rounded-full"
                          style={{width: `${value}%`}}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Vascular */}
          {metrics.vascular && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Vascular</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(metrics.vascular).map(([key, value]) => (
                  <div key={key}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="font-medium">{value}/100</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-emerald-600 h-2 rounded-full"
                        style={{width: `${value}%`}}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Key Findings */}
        {analysis.key_findings && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Key Findings</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {(Array.isArray(analysis.key_findings) ? analysis.key_findings : 
                  typeof analysis.key_findings === 'object' ? Object.values(analysis.key_findings).flat() : [])
                  .map((finding, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-1">•</span>
                    <span className="text-sm">{typeof finding === 'string' ? finding : JSON.stringify(finding)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4">
          {!showTreatments && (
            <Button
              onClick={generateTreatmentPlan}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="mr-2 h-4 w-4" />}
              Generate Treatment Plan
            </Button>
          )}
          <Button
            onClick={() => navigate('/book')}
            variant="outline"
            className="flex-1"
          >
            Schedule Consultation
          </Button>
        </div>

        {/* Treatment Plan Section */}
        {showTreatments && treatmentPlan && (
          <div className="mt-8">
            <TreatmentPlanDisplay plan={treatmentPlan} />
          </div>
        )}
      </div>
    </div>
  );
};

const TreatmentPlanDisplay = ({ plan }) => {
  const [activeTab, setActiveTab] = useState('professional');

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold">Your Personalized Treatment Plan</h2>

      {/* Tabs */}
      <div className="flex gap-4 border-b">
        <button
          onClick={() => setActiveTab('professional')}
          className={`pb-3 px-4 font-medium ${activeTab === 'professional' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-slate-600'}`}
        >
          Professional Treatments
        </button>
        <button
          onClick={() => setActiveTab('peptides')}
          className={`pb-3 px-4 font-medium ${activeTab === 'peptides' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-slate-600'}`}
        >
          Peptide Products
        </button>
        {plan.pepfactor_protocol && (
          <button
            onClick={() => setActiveTab('pepfactor')}
            className={`pb-3 px-4 font-medium ${activeTab === 'pepfactor' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-slate-600'}`}
          >
            PepFactor Protocol
          </button>
        )}
        <button
          onClick={() => setActiveTab('timeline')}
          className={`pb-3 px-4 font-medium ${activeTab === 'timeline' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-slate-600'}`}
        >
          Timeline
        </button>
      </div>

      {/* Professional Treatments */}
      {activeTab === 'professional' && (
        <div className="space-y-4">
          {plan.professional_treatments && plan.professional_treatments.map((treatment, idx) => (
            <Card key={idx} className="border-2 border-emerald-200">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{treatment.name}</CardTitle>
                    <Badge className="mt-2">{treatment.type}</Badge>
                  </div>
                  {!treatment.contraindicated?.cleared && (
                    <Badge className="bg-amber-100 text-amber-700">Screening Required</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-slate-700">Protocol:</p>
                    <p className="text-sm">{treatment.protocol}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-slate-700">Duration:</p>
                      <p className="text-sm">{treatment.duration}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">Expected Results:</p>
                      <p className="text-sm">{treatment.expected_results}</p>
                    </div>
                  </div>
                  {treatment.notes && (
                    <div className="p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-800">{treatment.notes}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Peptide Products */}
      {activeTab === 'peptides' && (
        <div className="space-y-4">
          {plan.peptide_products && plan.peptide_products.map((product, idx) => (
            <Card key={idx}>
              <CardHeader>
                <CardTitle className="text-lg">{product.peptide || product.type}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {product.mechanism && (
                    <div>
                      <p className="text-sm font-medium text-slate-700">Mechanism:</p>
                      <p className="text-sm">{product.mechanism}</p>
                    </div>
                  )}
                  {product.concentration && (
                    <div>
                      <p className="text-sm font-medium text-slate-700">Concentration:</p>
                      <p className="text-sm">{product.concentration}</p>
                    </div>
                  )}
                  {product.benefits && (
                    <div>
                      <p className="text-sm font-medium text-slate-700">Benefits:</p>
                      <p className="text-sm">{product.benefits}</p>
                    </div>
                  )}
                  {product.application && (
                    <div>
                      <p className="text-sm font-medium text-slate-700">Application:</p>
                      <p className="text-sm">{product.application}</p>
                    </div>
                  )}
                  {product.clinical_evidence && (
                    <div className="p-3 bg-emerald-50 rounded-lg">
                      <p className="text-sm text-emerald-800"><strong>Clinical Evidence:</strong> {product.clinical_evidence}</p>
                    </div>
                  )}
                  {product.morning && (
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div className="p-3 bg-amber-50 rounded-lg">
                        <p className="text-sm font-medium text-amber-800 mb-1">Morning:</p>
                        <p className="text-xs text-amber-700">{product.morning}</p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm font-medium text-blue-800 mb-1">Evening:</p>
                        <p className="text-xs text-blue-700">{product.evening}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* PepFactor Protocol */}
      {activeTab === 'pepfactor' && plan.pepfactor_protocol && (
        <Card className="border-2 border-purple-200">
          <CardHeader>
            <CardTitle className="text-2xl">{plan.pepfactor_protocol.product}</CardTitle>
            <CardDescription className="text-base">
              Professional-Grade Treatment System
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <p className="font-medium text-slate-700 mb-2">Components:</p>
                <p className="text-sm">{plan.pepfactor_protocol.components}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-medium text-slate-700 mb-2">Delivery Method:</p>
                  <p className="text-sm">{plan.pepfactor_protocol.delivery}</p>
                </div>
                <div>
                  <p className="font-medium text-slate-700 mb-2">Protocol:</p>
                  <p className="text-sm">{plan.pepfactor_protocol.protocol}</p>
                </div>
              </div>
              <div>
                <p className="font-medium text-slate-700 mb-2">Expected Results:</p>
                <p className="text-sm">{plan.pepfactor_protocol.expected_results}</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <p className="font-medium text-purple-900 mb-2">Targets:</p>
                <ul className="space-y-1">
                  {plan.pepfactor_protocol.targets.map((target, idx) => (
                    <li key={idx} className="text-sm text-purple-800">• {target}</li>
                  ))}
                </ul>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Clinical Evidence:</strong> {plan.pepfactor_protocol.clinical_evidence}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Timeline */}
      {activeTab === 'timeline' && (
        <Card>
          <CardHeader>
            <CardTitle>Treatment Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(plan.timeline || {}).map(([phase, description]) => (
                <div key={phase} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 bg-emerald-600 rounded-full" />
                    <div className="w-0.5 h-full bg-emerald-200" />
                  </div>
                  <div className="flex-1 pb-4">
                    <p className="font-medium capitalize text-slate-700">{phase.replace(/_/g, ' ')}</p>
                    <p className="text-sm text-slate-600 mt-1">{description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== PHOTO COMPARISON PAGE ====================

const PhotoComparisonPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const photoIds = location.state?.photoIds || [];
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [analysisFocus, setAnalysisFocus] = useState('general');

  const handleCompare = async () => {
    if (photoIds.length < 2) {
      toast.error('Please select at least 2 photos');
      navigate('/progress');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/capture/compare`, {
        photo_ids: photoIds,
        analysis_focus: analysisFocus
      });
      setReport(response.data);
      toast.success('Progress report generated!');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (photoIds.length >= 2) {
      handleCompare();
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mb-4" />
        <p className="text-lg font-medium">Analyzing your progress...</p>
        <p className="text-sm text-slate-600">This may take 30-60 seconds</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-12 text-center">
            <p className="text-slate-500">Unable to generate report</p>
            <Button onClick={() => navigate('/progress')} className="mt-4">
              Back to Gallery
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-12">
        <button
          onClick={() => navigate('/progress')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
        >
          <ArrowRight className="h-4 w-4 rotate-180" />
          Back to Gallery
        </button>

        <div className="mb-8 text-center">
          <Badge className="mb-4 bg-emerald-100 text-emerald-700">
            Progress Report
          </Badge>
          <h1 className="text-4xl font-bold mb-2">Your Progress Analysis</h1>
          <p className="text-slate-600">
            Comparing {photoIds.length} photos • {report.meta?.analyzed_at && new Date(report.meta.analyzed_at).toLocaleDateString()}
          </p>
        </div>

        {/* Overall Score */}
        <Card className="mb-6 border-2 border-emerald-200 bg-emerald-50">
          <CardContent className="p-8 text-center">
            <p className="text-sm font-medium text-emerald-800 mb-2">Overall Progress Score</p>
            <div className="text-6xl font-bold text-emerald-600 mb-2">
              {report.overall_progress_score || 0}/100
            </div>
            <p className="text-sm text-emerald-700">
              {report.overall_progress_score >= 75 ? 'Excellent progress!' :
               report.overall_progress_score >= 50 ? 'Good progress on track' :
               'Continue your treatment plan'}
            </p>
          </CardContent>
        </Card>

        {/* Areas of Improvement */}
        {report.areas_of_improvement && report.areas_of_improvement.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Check className="h-5 w-5 text-emerald-600" />
                Areas of Improvement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {report.areas_of_improvement.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                    <span className="text-sm">{typeof item === 'string' ? item : item.description || JSON.stringify(item)}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Areas of Concern */}
        {report.areas_of_concern && report.areas_of_concern.length > 0 && (
          <Card className="mb-6 border-amber-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <Badge className="bg-amber-100 text-amber-700">Attention Needed</Badge>
                Areas of Concern
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {report.areas_of_concern.map((item, idx) => (
                  <li key={idx} className="text-sm text-amber-800">
                    • {typeof item === 'string' ? item : item.description || JSON.stringify(item)}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Recommendations */}
        {report.recommendations && report.recommendations.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Recommendations</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {report.recommendations.map((rec, idx) => (
                  <li key={idx} className="text-sm">
                    {idx + 1}. {typeof rec === 'string' ? rec : rec.description || JSON.stringify(rec)}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex gap-4">
          <Button onClick={() => navigate('/book')} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
            Schedule Consultation
          </Button>
          <Button onClick={() => navigate('/progress')} variant="outline" className="flex-1">
            View All Photos
          </Button>
        </div>
      </div>
    </div>
  );
};

// ==================== RECEIPT PAGE ====================

const ReceiptPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  // Support both query param and location state
  const ridFromQuery = searchParams.get('rid');
  const { type, id } = location.state || {};
  const receiptId = ridFromQuery || id;
  const receiptType = type || (ridFromQuery ? 'appointment' : null);
  
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    console.log('🧾 ReceiptPage mounted');
    console.log('Query param rid:', ridFromQuery);
    console.log('Location state:', { type, id });
    console.log('Final receiptId:', receiptId);
    console.log('Final receiptType:', receiptType);
    
    if (receiptType && receiptId) {
      fetchReceipt();
    } else {
      console.warn('⚠️ Missing receiptType or receiptId');
      setLoading(false);
    }
  }, [receiptType, receiptId]);

  const fetchReceipt = async () => {
    console.log('🔄 Fetching receipt...');
    try {
      const endpoint = receiptType === 'appointment' 
        ? `/demo/receipt/appointment/${receiptId}`
        : `/demo/receipt/subscription/${receiptId}`;
      
      console.log('API endpoint:', `${API}${endpoint}`);
      const response = await axios.get(`${API}${endpoint}`);
      console.log('✅ Receipt loaded successfully');
      setReceipt(response.data);
    } catch (error) {
      console.error('❌ Failed to load receipt:', error);
      toast.error('Failed to load receipt');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailReceipt = () => {
    console.log('📧 Email Receipt button clicked');
    console.log('Receipt ID:', receiptId);
    console.log('Receipt Type:', receiptType);
    
    try {
      if (!receiptId) {
        console.error('❌ No receipt ID available');
        toast.error('No receipt found to email');
        return;
      }
      
      console.log('✅ Navigating to email preview with rid:', receiptId);
      toast.success('Opening email preview');
      navigate(`/email-preview?rid=${receiptId}`, {
        state: { type: receiptType, id: receiptId }
      });
    } catch (error) {
      console.error('❌ Email receipt error:', error);
      toast.error('Failed to open email preview');
    }
  };

  const handleDownloadPdf = async () => {
    console.log('📄 Download PDF button clicked');
    console.log('Receipt ID:', receiptId);
    console.log('Receipt Data:', receipt ? 'Available' : 'Missing');
    
    try {
      if (!receiptId || !receipt) {
        console.error('❌ Missing receipt ID or data');
        toast.error('No receipt found to download');
        return;
      }

      console.log('📦 Starting PDF generation...');
      
      // Dynamically import jsPDF
      const { jsPDF } = await import('jspdf');
      console.log('✅ jsPDF loaded successfully');
      
      const doc = new jsPDF();
      
      // Set font sizes
      const titleSize = 20;
      const headerSize = 12;
      const normalSize = 10;
      const smallSize = 8;
      
      let y = 20;
      
      // Title
      doc.setFontSize(titleSize);
      doc.setFont('helvetica', 'bold');
      doc.text('RECEIPT', 105, y, { align: 'center' });
      y += 10;
      
      // Receipt Number
      doc.setFontSize(normalSize);
      doc.setFont('helvetica', 'normal');
      doc.text(receipt.receipt_number, 105, y, { align: 'center' });
      y += 15;
      
      // PAID Badge
      doc.setFillColor(16, 185, 129);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(headerSize);
      doc.setFont('helvetica', 'bold');
      doc.rect(160, y - 5, 30, 8, 'F');
      doc.text('PAID', 175, y, { align: 'center' });
      doc.setTextColor(0, 0, 0);
      y += 15;
      
      // Bill To
      doc.setFontSize(headerSize);
      doc.setFont('helvetica', 'bold');
      doc.text('Bill To:', 20, y);
      y += 7;
      
      doc.setFontSize(normalSize);
      doc.setFont('helvetica', 'normal');
      doc.text(receipt.customer.name, 20, y);
      y += 5;
      doc.setFontSize(smallSize);
      doc.text(receipt.customer.email, 20, y);
      y += 10;
      
      // Service Location (if exists)
      if (receipt.clinic) {
        doc.setFontSize(headerSize);
        doc.setFont('helvetica', 'bold');
        doc.text('Service Location:', 110, y - 17);
        
        doc.setFontSize(normalSize);
        doc.setFont('helvetica', 'normal');
        doc.text(receipt.clinic.name, 110, y - 10);
        doc.setFontSize(smallSize);
        doc.text(receipt.clinic.address, 110, y - 5);
        doc.text(`${receipt.clinic.city}, ${receipt.clinic.state} ${receipt.clinic.zip_code}`, 110, y);
      }
      y += 5;
      
      // Appointment Details (if exists)
      if (receipt.appointment) {
        y += 5;
        doc.setDrawColor(200, 200, 200);
        doc.line(20, y, 190, y);
        y += 10;
        
        doc.setFontSize(headerSize);
        doc.setFont('helvetica', 'bold');
        doc.text('Appointment Details:', 20, y);
        y += 7;
        
        doc.setFontSize(normalSize);
        doc.setFont('helvetica', 'normal');
        doc.text(`Date: ${new Date(receipt.appointment.date).toLocaleDateString()}`, 20, y);
        y += 5;
        doc.text(`Service: ${receipt.appointment.service}`, 20, y);
        y += 10;
      }
      
      // Line Items Table
      y += 5;
      doc.line(20, y, 190, y);
      y += 10;
      
      doc.setFontSize(headerSize);
      doc.setFont('helvetica', 'bold');
      doc.text('Description', 20, y);
      doc.text('Qty', 130, y, { align: 'center' });
      doc.text('Amount', 190, y, { align: 'right' });
      y += 2;
      
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.5);
      doc.line(20, y, 190, y);
      y += 7;
      
      // Items
      doc.setFontSize(normalSize);
      doc.setFont('helvetica', 'normal');
      receipt.line_items.forEach(item => {
        doc.text(item.description, 20, y);
        doc.text(String(item.quantity), 130, y, { align: 'center' });
        doc.text(`$${(item.total / 100).toFixed(2)}`, 190, y, { align: 'right' });
        y += 6;
      });
      
      y += 3;
      doc.setLineWidth(0.2);
      doc.line(20, y, 190, y);
      y += 10;
      
      // Totals
      doc.text('Subtotal:', 130, y);
      doc.text(`$${(receipt.subtotal / 100).toFixed(2)}`, 190, y, { align: 'right' });
      y += 6;
      
      doc.text('Tax:', 130, y);
      doc.text(`$${(receipt.tax / 100).toFixed(2)}`, 190, y, { align: 'right' });
      y += 6;
      
      doc.setLineWidth(0.5);
      doc.line(130, y, 190, y);
      y += 7;
      
      doc.setFontSize(headerSize);
      doc.setFont('helvetica', 'bold');
      doc.text('Total:', 130, y);
      doc.setTextColor(16, 185, 129);
      doc.text(`$${(receipt.total / 100).toFixed(2)}`, 190, y, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      y += 15;
      
      // Payment Information
      doc.setFillColor(248, 250, 252);
      doc.rect(20, y - 5, 170, 25, 'F');
      
      y += 2;
      doc.setFontSize(headerSize);
      doc.setFont('helvetica', 'bold');
      doc.text('Payment Information', 25, y);
      y += 7;
      
      doc.setFontSize(normalSize);
      doc.setFont('helvetica', 'normal');
      doc.text(`Payment Method: ${receipt.payment_method}`, 25, y);
      y += 5;
      doc.setFontSize(smallSize);
      doc.text(`Payment ID: ${receipt.payment_id}`, 25, y);
      y += 5;
      doc.text('Status: PAID', 25, y);
      y += 15;
      
      // Footer
      doc.setFontSize(normalSize);
      doc.setTextColor(16, 185, 129);
      doc.text(receipt.footer_text, 105, y, { align: 'center' });
      y += 6;
      
      doc.setFontSize(smallSize);
      doc.setTextColor(100, 100, 100);
      doc.text(`Questions? Email ${receipt.support_email} or call ${receipt.support_phone}`, 105, y, { align: 'center' });
      
      // Save PDF
      console.log('💾 Saving PDF as:', `PepFactor-Receipt-${receiptId}.pdf`);
      doc.save(`PepFactor-Receipt-${receiptId}.pdf`);
      console.log('✅ PDF saved successfully!');
      toast.success('Receipt downloaded');
      
    } catch (error) {
      console.error('❌ PDF generation error:', error);
      toast.error('PDF generation failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-12 text-center">
            <p className="text-slate-500">Receipt not found</p>
            <Button onClick={() => navigate('/account')} className="mt-4">
              Back to Account
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <button
          onClick={() => navigate('/account')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
        >
          <ArrowRight className="h-4 w-4 rotate-180" />
          Back to Account
        </button>

        <Card className="border-2">
          <CardHeader className="bg-emerald-50 border-b-2">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-3xl">Receipt</CardTitle>
                <p className="text-slate-600 mt-2">{receipt.receipt_number}</p>
              </div>
              <Badge className="bg-emerald-600 text-white text-lg px-4 py-2">PAID</Badge>
            </div>
          </CardHeader>
          
          <CardContent className="p-8">
            {/* Header Info */}
            <div className="grid grid-cols-2 gap-8 mb-8 pb-8 border-b">
              <div>
                <h3 className="font-semibold text-slate-700 mb-2">Bill To:</h3>
                <p className="font-medium">{receipt.customer.name}</p>
                <p className="text-sm text-slate-600">{receipt.customer.email}</p>
              </div>
              {receipt.clinic && (
                <div>
                  <h3 className="font-semibold text-slate-700 mb-2">Service Location:</h3>
                  <p className="font-medium">{receipt.clinic.name}</p>
                  <p className="text-sm text-slate-600">{receipt.clinic.address}</p>
                  <p className="text-sm text-slate-600">{receipt.clinic.city}, {receipt.clinic.state} {receipt.clinic.zip_code}</p>
                </div>
              )}
            </div>

            {/* Appointment Details */}
            {receipt.appointment && (
              <div className="mb-8 pb-8 border-b">
                <h3 className="font-semibold text-slate-700 mb-4">Appointment Details:</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-600">Date:</span>
                    <span className="ml-2 font-medium">{new Date(receipt.appointment.date).toLocaleDateString()}</span>
                  </div>
                  <div>
                    <span className="text-slate-600">Service:</span>
                    <span className="ml-2 font-medium">{receipt.appointment.service}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Line Items */}
            <div className="mb-8">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2">
                    <th className="text-left py-3 font-semibold">Description</th>
                    <th className="text-center py-3 font-semibold">Qty</th>
                    <th className="text-right py-3 font-semibold">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {receipt.line_items.map((item, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="py-4">{item.description}</td>
                      <td className="text-center py-4">{item.quantity}</td>
                      <td className="text-right py-4">${(item.total / 100).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <div className="flex justify-end mb-8">
              <div className="w-64">
                <div className="flex justify-between py-2">
                  <span className="text-slate-600">Subtotal:</span>
                  <span className="font-medium">${(receipt.subtotal / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-slate-600">Tax:</span>
                  <span className="font-medium">${(receipt.tax / 100).toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-3 border-t-2 text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-emerald-600">${(receipt.total / 100).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Payment Info */}
            <div className="bg-slate-50 p-4 rounded-lg mb-6">
              <h3 className="font-semibold mb-2">Payment Information</h3>
              <div className="text-sm space-y-1">
                <p><span className="text-slate-600">Payment Method:</span> <span className="ml-2">{receipt.payment_method}</span></p>
                <p><span className="text-slate-600">Payment ID:</span> <span className="ml-2 font-mono text-xs">{receipt.payment_id}</span></p>
                <p><span className="text-slate-600">Status:</span> <Badge className="ml-2 bg-emerald-100 text-emerald-700">PAID</Badge></p>
              </div>
            </div>

            {/* Terms */}
            {receipt.terms && (
              <div className="border-t pt-6">
                <h3 className="font-semibold mb-3">Terms & Conditions:</h3>
                <ul className="text-sm text-slate-600 space-y-1">
                  {receipt.terms.map((term, idx) => (
                    <li key={idx}>• {term}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Footer */}
            <div className="mt-8 pt-6 border-t text-center">
              <p className="font-medium text-emerald-600 mb-2">{receipt.footer_text}</p>
              <p className="text-sm text-slate-600">
                Questions? Email {receipt.support_email} or call {receipt.support_phone}
              </p>
              <p className="text-xs text-slate-400 mt-4">
                This is a demo receipt. In production, this would be a PDF download.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-4 mt-8">
              <Button 
                type="button"
                onClick={(e) => {
                  console.log('🖱️ Download PDF button CLICKED - event:', e);
                  handleDownloadPdf();
                }}
                className="flex-1" 
                variant="outline"
              >
                Download PDF
              </Button>
              <Button 
                type="button"
                onClick={(e) => {
                  console.log('🖱️ Email Receipt button CLICKED - event:', e);
                  handleEmailReceipt();
                }}
                className="flex-1" 
                variant="outline"
              >
                Email Receipt
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== EMAIL PREVIEW PAGE ====================

const EmailPreviewPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  // Support both query param and location state
  const ridFromQuery = searchParams.get('rid');
  const { type, id } = location.state || {};
  const emailId = ridFromQuery || id;
  const emailType = type || (ridFromQuery ? 'appointment' : null);
  
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    if (emailType && emailId) {
      fetchEmail();
    } else {
      toast.error('No receipt found to email');
      setLoading(false);
    }
  }, [emailType, emailId]);

  const fetchEmail = async () => {
    try {
      const endpoint = emailType === 'appointment'
        ? `/demo/email/appointment/${emailId}`
        : `/demo/email/subscription/${emailId}`;
      
      const response = await axios.get(`${API}${endpoint}`);
      setEmail(response.data);
    } catch (error) {
      toast.error('Failed to load email preview');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!email) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-12 text-center">
            <p className="text-slate-500">Email not found</p>
            <Button onClick={() => navigate('/account')} className="mt-4">
              Back to Account
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <button
          onClick={() => navigate('/account')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
        >
          <ArrowRight className="h-4 w-4 rotate-180" />
          Back to Account
        </button>

        <Card>
          <CardHeader className="bg-blue-50 border-b">
            <CardTitle>Email Preview (Demo Mode)</CardTitle>
            <CardDescription className="text-base mt-2">
              <strong>To:</strong> {email.to}<br/>
              <strong>Subject:</strong> {email.subject}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="bg-slate-100 p-8">
              <div className="bg-white shadow-lg">
                <iframe
                  srcDoc={email.html}
                  className="w-full border-0"
                  style={{minHeight: '800px'}}
                  title="Email Preview"
                />
              </div>
            </div>
            <div className="p-6 bg-amber-50 border-t">
              <p className="text-sm text-amber-800">
                <strong>📧 Demo Mode:</strong> This email preview shows what customers would receive. 
                In production, emails are sent via SendGrid/AWS SES.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== CALENDAR PAGE ====================

const CalendarPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetchCalendarEvents();
  }, []);

  const fetchCalendarEvents = async () => {
    try {
      const response = await axios.get(`${API}/demo/calendar`);
      setEvents(response.data);
    } catch (error) {
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">My Calendar</h1>
          <p className="text-slate-600">View and manage your appointments</p>
        </div>

        {events.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Calendar className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 mb-4">No appointments scheduled</p>
              <Button onClick={() => navigate('/book')} className="bg-emerald-600 hover:bg-emerald-700">
                Book Your First Appointment
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {events.map((event) => (
              <Card key={event.id} className="border-2 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4 flex-1">
                      <div className="bg-emerald-100 rounded-lg p-4 text-center">
                        <div className="text-2xl font-bold text-emerald-600">
                          {new Date(event.start).getDate()}
                        </div>
                        <div className="text-sm text-emerald-700">
                          {new Date(event.start).toLocaleDateString('en-US', { month: 'short' })}
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold mb-2">{event.title}</h3>
                        <div className="space-y-2 text-sm text-slate-600">
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span>{new Date(event.start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            <span>{event.location}</span>
                          </div>
                          {event.deposit_paid && (
                            <div className="flex items-center gap-2">
                              <Check className="h-4 w-4 text-emerald-600" />
                              <span>Deposit paid: ${(event.amount / 100).toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Badge className="bg-emerald-100 text-emerald-700">
                        {event.status}
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate('/receipt', { state: { type: 'appointment', id: event.id } })}
                      >
                        View Receipt
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate('/email-preview', { state: { type: 'appointment', id: event.id } })}
                      >
                        View Email
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== ACCOUNT PAGE ====================

const AccountPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [appts, subs, pics] = await Promise.all([
        axios.get(`${API}/appointments`),
        axios.get(`${API}/subscriptions`),
        axios.get(`${API}/capture/photos`)
      ]);
      
      setAppointments(appts.data);
      setSubscriptions(subs.data);
      setPhotos(pics.data);
    } catch (error) {
      toast.error('Failed to load account data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2" data-testid="account-title">My Account</h1>
            <p className="text-slate-600" data-testid="account-email">{user?.email}</p>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="logout-btn">
            Log Out
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/progress')}>
            <CardHeader>
              <CardTitle>Photos & Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-emerald-600" data-testid="photos-count">{photos.length}</div>
              <p className="text-sm text-slate-600 mt-2">View timeline & compare</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/calendar')}>
            <CardHeader>
              <CardTitle>Appointments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-emerald-600" data-testid="appointments-count">{appointments.length}</div>
              <p className="text-sm text-slate-600 mt-2">View calendar</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Subscriptions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-emerald-600" data-testid="subscriptions-count">{subscriptions.length}</div>
              <p className="text-sm text-slate-600 mt-2">Active plans</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="text-blue-900">Demo Mode</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-blue-800">All payments simulated. View receipts & emails!</p>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Recent Activity</CardTitle>
              <Badge className="bg-blue-100 text-blue-700">Demo Mode Active</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {(appointments.length > 0 || subscriptions.length > 0 || photos.length > 0) ? (
              <div className="space-y-4">
                {appointments.slice(0, 3).map((apt, idx) => (
                  <div key={apt.id} className="flex items-center justify-between border-b pb-3">
                    <div className="flex-1">
                      <p className="font-medium">Appointment Confirmed</p>
                      <p className="text-sm text-slate-600">
                        {new Date(apt.start).toLocaleDateString()} • $20 deposit paid
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate('/receipt', { state: { type: 'appointment', id: apt.id } })}
                      >
                        Receipt
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate('/email-preview', { state: { type: 'appointment', id: apt.id } })}
                      >
                        Email
                      </Button>
                    </div>
                  </div>
                ))}
                
                {subscriptions.slice(0, 3).map((sub, idx) => (
                  <div key={sub.id} className="flex items-center justify-between border-b pb-3">
                    <div className="flex-1">
                      <p className="font-medium">Subscription Started</p>
                      <p className="text-sm text-slate-600">
                        {sub.sku} • {new Date(sub.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate('/receipt', { state: { type: 'subscription', id: sub.id } })}
                      >
                        Receipt
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate('/email-preview', { state: { type: 'subscription', id: sub.id } })}
                      >
                        Email
                      </Button>
                    </div>
                  </div>
                ))}
                
                {photos.slice(0, 2).map((photo, idx) => (
                  <div key={photo.id} className="flex items-center justify-between border-b pb-3" data-testid={`photo-item-${idx}`}>
                    <div>
                      <p className="font-medium">Photo captured</p>
                      <p className="text-sm text-slate-600">{photo.type} • {new Date(photo.captured_at).toLocaleDateString()}</p>
                    </div>
                    {photo.ai_metrics && (
                      <Badge className="bg-emerald-100 text-emerald-700">AI Analyzed</Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-8">No activity yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ==================== PROVIDER ONBOARDING (PRIVATE) ====================

const ProviderOnboardingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [inviteValid, setInviteValid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [spaId, setSpaId] = useState(null);
  const [selectedTier, setSelectedTier] = useState(null);
  
  // Step 1 form data
  const [formData, setFormData] = useState({
    spa_name: '',
    owner_name: '',
    email: '',
    mobile: '',
    address: '',
    city: '',
    state: '',
    country: 'USA'
  });

  React.useEffect(() => {
    validateInvite();
  }, []);

  const validateInvite = async () => {
    const token = searchParams.get('inv');
    if (!token) {
      toast.error('Invalid invite link');
      navigate('/');
      return;
    }

    try {
      await axios.post(`${API}/pep-provider/validate-invite`, null, {
        params: { invite_token: token }
      });
      setInviteValid(true);
    } catch (error) {
      toast.error('Invalid or expired invite link');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/pep-provider/register`, formData);
      setSpaId(response.data.spa_id);
      setStep(2);
      toast.success('Spa registered! Now choose your tier.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleTierSelect = async (tier) => {
    if (!spaId) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/pep-provider/tier-select`, {
        spa_id: spaId,
        tier: tier
      });
      setSelectedTier(tier);
      setStep(3);
      toast.success('Tier selected!');
    } catch (error) {
      toast.error('Tier selection failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (!spaId || !selectedTier) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/pep-provider/create-checkout`, null, {
        params: { spa_id: spaId, tier: selectedTier }
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Checkout failed');
      setLoading(false);
    }
  };

  if (loading && !inviteValid) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!inviteValid) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="h-10 w-10 text-emerald-600" />
            <h1 className="text-4xl font-bold">PepFactor USA</h1>
          </div>
          <h2 className="text-2xl font-semibold mb-2">Provider Pre-Order Program</h2>
          <p className="text-slate-600">Join the exclusive launch cohort • Go-Live: February 28, 2026</p>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-emerald-600' : 'text-slate-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-emerald-600 text-white' : 'bg-slate-200'}`}>
              1
            </div>
            <span className="font-medium">Spa Details</span>
          </div>
          <div className="w-12 h-0.5 bg-slate-300"></div>
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-emerald-600' : 'text-slate-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-emerald-600 text-white' : 'bg-slate-200'}`}>
              2
            </div>
            <span className="font-medium">Choose Tier</span>
          </div>
          <div className="w-12 h-0.5 bg-slate-300"></div>
          <div className={`flex items-center gap-2 ${step >= 3 ? 'text-emerald-600' : 'text-slate-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-emerald-600 text-white' : 'bg-slate-200'}`}>
              3
            </div>
            <span className="font-medium">Payment</span>
          </div>
        </div>

        {/* Step 1: Spa Details */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Spa Information</CardTitle>
              <CardDescription>Tell us about your med spa</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleStep1Submit} className="space-y-4">
                <div>
                  <Label htmlFor="spa_name">Spa Name *</Label>
                  <Input
                    id="spa_name"
                    value={formData.spa_name}
                    onChange={(e) => setFormData({...formData, spa_name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="owner_name">Owner / Medical Director Name *</Label>
                  <Input
                    id="owner_name"
                    value={formData.owner_name}
                    onChange={(e) => setFormData({...formData, owner_name: e.target.value})}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="mobile">Mobile *</Label>
                    <Input
                      id="mobile"
                      type="tel"
                      value={formData.mobile}
                      onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="address">Address *</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      required
                      placeholder="CA"
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Tier Selection */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-semibold mb-2">Choose Your Pre-Order Tier</h3>
              <p className="text-slate-600">Select the tier that best fits your practice</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Tier 1 */}
              <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleTierSelect('2500')}>
                <CardHeader className="bg-slate-50">
                  <CardTitle className="text-lg">Launch Provider</CardTitle>
                  <div className="text-3xl font-bold text-emerald-600">$2,500</div>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div>
                    <p className="font-semibold mb-1">33 Kit Credits</p>
                    <p className="text-sm text-slate-600">$75/kit effective price</p>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600">✓</span>
                      <span>Listed in PepFactor App</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600">✓</span>
                      <span>Protocols & clinical assets</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600">✓</span>
                      <span>Basic marketing pack</span>
                    </div>
                  </div>
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading}>
                    Select Tier 1
                  </Button>
                </CardContent>
              </Card>

              {/* Tier 2 */}
              <Card className="cursor-pointer hover:shadow-lg transition-shadow border-emerald-200" onClick={() => handleTierSelect('5000')}>
                <CardHeader className="bg-emerald-50">
                  <CardTitle className="text-lg">Growth Provider</CardTitle>
                  <div className="text-3xl font-bold text-emerald-600">$5,000</div>
                  <Badge className="bg-emerald-600 text-white">Popular</Badge>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div>
                    <p className="font-semibold mb-1">72 Kit Credits</p>
                    <p className="text-sm text-slate-600">$69/kit effective price</p>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600">✓</span>
                      <span>Priority listing above Tier 1</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600">✓</span>
                      <span>Extra marketing assets</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600">✓</span>
                      <span>AI Assistant add-on ($20/mo)</span>
                    </div>
                  </div>
                  <Button className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading}>
                    Select Tier 2
                  </Button>
                </CardContent>
              </Card>

              {/* Tier 3 */}
              <Card className="cursor-pointer hover:shadow-lg transition-shadow border-emerald-400 border-2" onClick={() => handleTierSelect('7500')}>
                <CardHeader className="bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
                  <CardTitle className="text-lg">Founding Partner</CardTitle>
                  <div className="text-3xl font-bold">$7,500</div>
                  <Badge className="bg-white text-emerald-700">Best Value</Badge>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div>
                    <p className="font-semibold mb-1">127 Kit Credits</p>
                    <p className="text-sm text-slate-600">$59/kit effective price</p>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600">✓</span>
                      <span><strong>Founding Partner badge</strong></span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600">✓</span>
                      <span><strong>PepFactor Number + AI included</strong></span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600">✓</span>
                      <span><strong>10% lifetime discount</strong></span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-emerald-600">✓</span>
                      <span>Priority for future KOL status</span>
                    </div>
                  </div>
                  <Button className="w-full bg-emerald-700 hover:bg-emerald-800" disabled={loading}>
                    Select Tier 3
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {step === 3 && selectedTier && (
          <Card>
            <CardHeader>
              <CardTitle>Complete Your Pre-Order</CardTitle>
              <CardDescription>Secure your spot in the February 2026 launch cohort</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-emerald-50 p-6 rounded-lg">
                <h4 className="font-semibold mb-4">Your Selection:</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Tier:</span>
                    <span className="font-semibold">
                      {selectedTier === '2500' ? 'Launch Provider' : selectedTier === '5000' ? 'Growth Provider' : 'Founding Partner'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Kit Credits:</span>
                    <span className="font-semibold">
                      {selectedTier === '2500' ? '33' : selectedTier === '5000' ? '72' : '127'} kits
                    </span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t">
                    <span>Pre-Order Amount:</span>
                    <span className="text-emerald-600">${parseInt(selectedTier).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-semibold">What's Next:</h4>
                <div className="space-y-2 text-sm text-slate-600">
                  <p>✓ Secure payment via Stripe</p>
                  <p>✓ Immediate confirmation & spa listing as "Coming Soon"</p>
                  <p>✓ AI assistant & calendar setup (Dec 2025 - Jan 2026)</p>
                  <p>✓ First kit shipment: ~February 15, 2026</p>
                  <p>✓ Official go-live: February 28, 2026</p>
                </div>
              </div>

              <Button 
                onClick={handleCheckout} 
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-lg py-6"
                disabled={loading}
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Proceed to Payment'}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

const ProviderSuccessPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center px-6">
      <Card className="max-w-2xl w-full">
        <CardContent className="p-12 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl">🎉</span>
          </div>
          <h1 className="text-3xl font-bold mb-4">Welcome to PepFactor!</h1>
          <p className="text-lg text-slate-600 mb-6">
            Your pre-order has been confirmed. You're now part of the exclusive February 2026 launch cohort.
          </p>
          <div className="bg-slate-50 p-6 rounded-lg mb-6 text-left">
            <h3 className="font-semibold mb-3">Next Steps:</h3>
            <div className="space-y-2 text-sm text-slate-600">
              <p>✓ Check your email for confirmation details</p>
              <p>✓ We'll contact you in December 2025 for AI & calendar setup</p>
              <p>✓ Your spa will be listed as "Coming Soon" in the app</p>
              <p>✓ First kit shipment: ~February 15, 2026</p>
              <p>✓ Official launch: February 28, 2026</p>
            </div>
          </div>
          <Button onClick={() => navigate('/')} className="bg-emerald-600 hover:bg-emerald-700">
            Return to Home
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== PROVIDER PORTAL ====================

const ProviderPortalPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Check if user is a provider (has clinic ownership or is clinic_owner/admin)
  const isProvider = user && (user.role === 'clinic_owner' || user.role === 'admin');

  if (!user) {
    // Not logged in - show Apply and Login buttons
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Sparkles className="h-12 w-12 text-emerald-600" />
            </div>
            <h1 className="text-4xl font-bold mb-4">Provider Portal</h1>
            <p className="text-xl text-slate-600">Join the PepFactor USA network as a med spa provider</p>
          </div>

          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-12">
              <div className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-semibold mb-2">Get Started</h2>
                  <p className="text-slate-600">Already a partner or new to PepFactor?</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button 
                    onClick={() => navigate('/provider/apply')}
                    className="h-24 text-lg bg-emerald-600 hover:bg-emerald-700"
                  >
                    <div className="text-center">
                      <div className="font-bold">Apply to Join</div>
                      <div className="text-sm font-normal mt-1">New Provider</div>
                    </div>
                  </Button>
                  
                  <Button 
                    onClick={() => navigate('/provider/login')}
                    className="h-24 text-lg"
                    variant="outline"
                  >
                    <div className="text-center">
                      <div className="font-bold">Provider Login</div>
                      <div className="text-sm font-normal mt-1">Existing Partner</div>
                    </div>
                  </Button>
                </div>

                <div className="mt-8 p-6 bg-emerald-50 rounded-lg">
                  <h3 className="font-semibold mb-3">Benefits of Becoming a Provider</h3>
                  <ul className="space-y-2 text-sm text-slate-700">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 mt-0.5">✓</span>
                      <span>Access to exclusive PepFactor protocols and treatments</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 mt-0.5">✓</span>
                      <span>AI-powered patient consultation tools</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 mt-0.5">✓</span>
                      <span>Direct patient lead generation from app</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-600 mt-0.5">✓</span>
                      <span>Marketing and promotional support</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!isProvider) {
    // Logged in but not a provider
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-12">
          <Card>
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">🏥</span>
              </div>
              <h2 className="text-2xl font-bold mb-4">Provider Portal Access</h2>
              <p className="text-lg text-slate-600 mb-6">
                You are currently a Consumer. Apply to become a Provider to access clinic features.
              </p>
              <Button 
                onClick={() => navigate('/provider/apply')}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Apply to Become a Provider
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Provider Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Provider Dashboard</h1>
          <p className="text-slate-600">Manage your clinic and patient leads</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/provider/leads')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">👥</span>
                </div>
                <div>
                  <div className="text-2xl font-bold">0</div>
                  <div className="text-sm text-slate-600">New Leads</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/provider/order')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">📦</span>
                </div>
                <div>
                  <div className="text-2xl font-bold">0</div>
                  <div className="text-sm text-slate-600">Active Orders</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate('/provider/profile')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">🏥</span>
                </div>
                <div>
                  <div className="text-sm font-semibold">Clinic Profile</div>
                  <div className="text-xs text-slate-600">View & Edit</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button onClick={() => navigate('/provider/order')} className="w-full justify-start" variant="outline">
                📦 Place New Order
              </Button>
              <Button onClick={() => navigate('/provider/leads')} className="w-full justify-start" variant="outline">
                👥 View Patient Leads
              </Button>
              <Button onClick={() => navigate('/provider/profile')} className="w-full justify-start" variant="outline">
                ⚙️ Update Clinic Profile
              </Button>
              <Button onClick={() => navigate('/provider/help')} className="w-full justify-start" variant="outline">
                ❓ Get Support
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 text-center py-8">No recent activity</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const ProviderApplyPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    clinicName: '',
    ownerName: '',
    email: user?.email || '',
    phone: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    website: '',
    instagram: '',
    monthlyVolume: '0-100',
    services: [],
    canCapturePhotos: false,
    agreesMarketing: false,
    readyPreorder: false,
    notes: ''
  });

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const payload = {
        clinic_name: formData.clinicName,
        owner_name: formData.ownerName,
        email: formData.email,
        phone: formData.phone,
        street_address: formData.streetAddress,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        website: formData.website || null,
        instagram: formData.instagram || null,
        monthly_volume: formData.monthlyVolume,
        services: formData.services,
        can_capture_photos: formData.canCapturePhotos,
        agrees_marketing: formData.agreesMarketing,
        ready_preorder: formData.readyPreorder,
        notes: formData.notes || null
      };
      
      const response = await axios.post(`${API}/provider/application`, payload, { headers });
      
      toast.success(response.data.message);
      setLoading(false);
      
      // Redirect to provider portal
      setTimeout(() => navigate('/provider'), 2000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Application submission failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Apply to Become a Provider</h1>
          <p className="text-slate-600">Join the PepFactor USA network</p>
        </div>

        <Card>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Clinic Name & Owner */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="clinicName">Clinic Name *</Label>
                  <Input
                    id="clinicName"
                    value={formData.clinicName}
                    onChange={(e) => setFormData({...formData, clinicName: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="ownerName">Owner/Decision-Maker Name *</Label>
                  <Input
                    id="ownerName"
                    value={formData.ownerName}
                    onChange={(e) => setFormData({...formData, ownerName: e.target.value})}
                    required
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <Label htmlFor="streetAddress">Street Address *</Label>
                <Input
                  id="streetAddress"
                  value={formData.streetAddress}
                  onChange={(e) => setFormData({...formData, streetAddress: e.target.value})}
                  required
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({...formData, state: e.target.value})}
                    required
                    placeholder="CA"
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label htmlFor="zipCode">ZIP Code *</Label>
                  <Input
                    id="zipCode"
                    value={formData.zipCode}
                    onChange={(e) => setFormData({...formData, zipCode: e.target.value})}
                    required
                  />
                </div>
              </div>

              {/* Social */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                    placeholder="https://"
                  />
                </div>
                <div>
                  <Label htmlFor="instagram">Instagram Handle</Label>
                  <Input
                    id="instagram"
                    value={formData.instagram}
                    onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                    placeholder="@yourhandle"
                  />
                </div>
              </div>

              {/* Monthly Volume */}
              <div>
                <Label htmlFor="monthlyVolume">Monthly Client Volume *</Label>
                <select
                  id="monthlyVolume"
                  value={formData.monthlyVolume}
                  onChange={(e) => setFormData({...formData, monthlyVolume: e.target.value})}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="0-100">0-100 clients/month</option>
                  <option value="100-300">100-300 clients/month</option>
                  <option value="300-600">300-600 clients/month</option>
                  <option value="600+">600+ clients/month</option>
                </select>
              </div>

              {/* Services */}
              <div>
                <Label>Services Offered *</Label>
                <div className="mt-2 space-y-2">
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.services.includes('hair_scalp')}
                      onCheckedChange={() => handleServiceToggle('hair_scalp')}
                    />
                    <span className="text-sm">Hair/Scalp Treatments</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.services.includes('skin')}
                      onCheckedChange={() => handleServiceToggle('skin')}
                    />
                    <span className="text-sm">Skin Treatments</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.services.includes('acne')}
                      onCheckedChange={() => handleServiceToggle('acne')}
                    />
                    <span className="text-sm">Acne Appearance Treatments</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <Checkbox
                      checked={formData.services.includes('other')}
                      onCheckedChange={() => handleServiceToggle('other')}
                    />
                    <span className="text-sm">Other Services</span>
                  </label>
                </div>
              </div>

              {/* Requirements */}
              <div className="space-y-4 border-t pt-6">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="canCapturePhotos"
                    checked={formData.canCapturePhotos}
                    onCheckedChange={(checked) => setFormData({...formData, canCapturePhotos: checked})}
                    required
                  />
                  <div>
                    <Label htmlFor="canCapturePhotos" className="font-semibold cursor-pointer">
                      Can capture standardized photos at Baseline, Day 30, and Day 90? *
                    </Label>
                    <p className="text-xs text-slate-600 mt-1">Required for tracking patient progress</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="agreesMarketing"
                    checked={formData.agreesMarketing}
                    onCheckedChange={(checked) => setFormData({...formData, agreesMarketing: checked})}
                    required
                  />
                  <div>
                    <Label htmlFor="agreesMarketing" className="font-semibold cursor-pointer">
                      Agree to appearance-only marketing guidelines? *
                    </Label>
                    <p className="text-xs text-slate-600 mt-1">All claims must be appearance-based, not medical</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="readyPreorder"
                    checked={formData.readyPreorder}
                    onCheckedChange={(checked) => setFormData({...formData, readyPreorder: checked})}
                  />
                  <div>
                    <Label htmlFor="readyPreorder" className="font-semibold cursor-pointer">
                      Ready to place Founding KOL pre-order?
                    </Label>
                    <p className="text-xs text-slate-600 mt-1">Optional: Become a founding provider with special benefits</p>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label htmlFor="notes">Additional Notes</Label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="w-full min-h-[120px] px-3 py-2 border border-slate-300 rounded-md"
                  placeholder="Any additional information you'd like to share..."
                />
              </div>

              {/* Submit */}
              <div className="flex gap-4">
                <Button type="button" variant="outline" onClick={() => navigate('/provider')} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Submit Application'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const ProviderLoginPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-6">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="h-8 w-8 text-emerald-600" />
            <span className="font-bold text-2xl">PepFactor USA</span>
          </div>
          <CardTitle>Provider Login</CardTitle>
          <CardDescription>Sign in to your provider account</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-center text-slate-600 mb-6">
            Provider login uses the same authentication system.
          </p>
          <Button onClick={() => navigate('/auth')} className="w-full bg-emerald-600 hover:bg-emerald-700">
            Go to Login
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

const ProviderOrderPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [lastOrderedProduct, setLastOrderedProduct] = useState(null);
  
  React.useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      // Products are already sorted by sort_order on backend
      const response = await axios.get(`${API}/products?category=provider&active_only=true`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleOrder = async (product) => {
    try {
      // Create order intent in database (includes userId, clinicId, productSku, productName, price, currency, createdAt, status)
      const response = await axios.post(`${API}/provider/order-intent?product_id=${product.id}`);
      const { order_intent_id, product: orderProduct, demo_mode } = response.data;
      
      // Set order placed state to show confirmation message
      setOrderPlaced(true);
      setLastOrderedProduct(product);
      
      // Check if demo mode (either from env or product checkout_mode)
      if (demo_mode || orderProduct.checkout_mode === 'demo') {
        toast.success('Mock checkout complete!', {
          description: `Order ID: ${order_intent_id}`
        });
        return;
      }
      
      // If stripe_payment_link mode and URL exists, open in new tab
      if (orderProduct.checkout_mode === 'stripe_payment_link' && orderProduct.payment_link_url) {
        toast.info('Opening Stripe checkout...', {
          description: 'Complete your payment in the new tab'
        });
        window.open(orderProduct.payment_link_url, '_blank', 'noopener,noreferrer');
        return;
      }
      
      // Fallback for other modes
      toast.info('Order recorded. Our team will contact you shortly.');
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Place Order</h1>
          <p className="text-slate-600">Order PepFactor products for your clinic</p>
        </div>

        {/* Confirmation message after order */}
        {orderPlaced && (
          <Card className="mb-8 border-2 border-emerald-400 bg-emerald-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center shrink-0">
                  <Check className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-emerald-900 mb-1">Order Initiated</h3>
                  <p className="text-emerald-800">
                    After payment, your Provider listing is activated within 72 hours.
                  </p>
                  {lastOrderedProduct && (
                    <p className="text-sm text-emerald-700 mt-2">
                      Product: {lastOrderedProduct.name} - ${lastOrderedProduct.price.toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {products.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">📦</span>
              </div>
              <h2 className="text-2xl font-bold mb-4">No Products Available</h2>
              <p className="text-slate-600 mb-6">
                Provider products will appear here once they are added by the admin.
              </p>
              <Button onClick={() => navigate('/provider')} variant="outline">
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {products.map(product => (
              <Card key={product.id} className="border-2 hover:border-emerald-400 transition-all">
                <CardHeader>
                  {product.image_url && (
                    <img src={product.image_url} alt={product.name} className="w-full h-48 object-cover rounded-lg mb-4" />
                  )}
                  <CardTitle className="text-xl">{product.name}</CardTitle>
                  <div className="text-3xl font-bold text-emerald-600">
                    ${product.price.toFixed(2)}
                  </div>
                  <Badge className="w-fit">{product.type === 'subscription' ? 'Subscription' : 'Bulk Order'}</Badge>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 mb-4">{product.description}</p>
                  <div className="text-xs text-slate-500 mb-4">
                    SKU: {product.sku}
                  </div>
                  <Button
                    onClick={() => handleOrder(product)}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid={`order-btn-${product.sku}`}
                  >
                    Order Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ProviderProfilePage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Clinic Profile</h1>
          <p className="text-slate-600">Manage your clinic information</p>
        </div>

        <Card>
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">🏥</span>
            </div>
            <h2 className="text-2xl font-bold mb-4">Profile Management Coming Soon</h2>
            <p className="text-slate-600 mb-6">
              Clinic profile management will be available soon. You'll be able to:
            </p>
            <ul className="text-left max-w-md mx-auto space-y-2 mb-8">
              <li className="flex items-start gap-2">
                <span className="text-emerald-600">✓</span>
                <span>Update clinic information and hours</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-600">✓</span>
                <span>Manage service offerings</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-600">✓</span>
                <span>Upload photos and credentials</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-600">✓</span>
                <span>Set booking availability</span>
              </li>
            </ul>
            <Button onClick={() => navigate('/provider')} variant="outline">
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const ProviderLeadsPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Patient Leads</h1>
          <p className="text-slate-600">Manage inquiries and consultations</p>
        </div>

        <Card>
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">👥</span>
            </div>
            <h2 className="text-2xl font-bold mb-4">No Leads Yet</h2>
            <p className="text-slate-600 mb-6">
              Patient leads will appear here when consumers express interest in your clinic through the PepFactor app.
            </p>
            <ul className="text-left max-w-md mx-auto space-y-2 mb-8">
              <li className="flex items-start gap-2">
                <span className="text-emerald-600">✓</span>
                <span>View consultation requests</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-600">✓</span>
                <span>Respond to patient inquiries</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-600">✓</span>
                <span>Track conversion rates</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-600">✓</span>
                <span>Schedule appointments</span>
              </li>
            </ul>
            <Button onClick={() => navigate('/provider')} variant="outline">
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const ProviderHelpPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Provider Support</h1>
          <p className="text-slate-600">Get help and resources</p>
        </div>

        <div className="grid gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Contact Support</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl">📧</span>
                <div>
                  <div className="font-semibold">Email Support</div>
                  <a href="mailto:providers@pepfactor.org" className="text-emerald-600 hover:underline">
                    providers@pepfactor.org
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-2xl">📞</span>
                <div>
                  <div className="font-semibold">Phone Support</div>
                  <a href="tel:+18788880840" className="text-emerald-600 hover:underline">
                    +1 878 888 0840
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Resources</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start">
                  📖 Training Materials
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  📋 Treatment Protocols
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  💰 Pricing & Billing
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  ❓ FAQs
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="text-center">
            <Button onClick={() => navigate('/provider')} variant="outline">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== PROVIDER MARKETPLACE (Feature Flag: FEATURE_PROVIDERS_ENABLED) ====================

// Note: useFeatureFlag and FeatureGate are now imported from @/hooks/useFeatureFlag and @/components/common/FeatureGate

// Provider Directory Page
const ProvidersDirectoryPage = () => {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [states, setStates] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ state: '', service: '' });

  React.useEffect(() => {
    fetchData();
  }, []);

  React.useEffect(() => {
    fetchProviders();
  }, [filters]);

  const fetchData = async () => {
    try {
      const [statesRes, servicesRes] = await Promise.all([
        axios.get(`${API}/marketplace/providers/states`),
        axios.get(`${API}/services`)
      ]);
      setStates(statesRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error('Failed to load data', error);
    }
  };

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.state) params.append('state', filters.state);
      if (filters.service) params.append('service', filters.service);
      
      const response = await axios.get(`${API}/marketplace/providers?${params}`);
      setProviders(response.data);
    } catch (error) {
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FeatureGate flag="FEATURE_PROVIDERS_ENABLED">
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">Find a Provider</h1>
            <p className="text-slate-600">Discover certified PepFactor providers near you</p>
          </div>

          {/* Filters */}
          <Card className="mb-8">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">State</label>
                  <select
                    className="w-full p-2 border rounded-lg"
                    value={filters.state}
                    onChange={(e) => setFilters(prev => ({ ...prev, state: e.target.value }))}
                  >
                    <option value="">All States</option>
                    {states.map(s => (
                      <option key={s.state} value={s.state}>{s.state} ({s.provider_count})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Service</label>
                  <select
                    className="w-full p-2 border rounded-lg"
                    value={filters.service}
                    onChange={(e) => setFilters(prev => ({ ...prev, service: e.target.value }))}
                  >
                    <option value="">All Services</option>
                    {services.map(s => (
                      <option key={s.slug} value={s.slug}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <Button onClick={fetchProviders} className="w-full bg-emerald-600 hover:bg-emerald-700">
                    <Search className="h-4 w-4 mr-2" /> Search
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : providers.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MapPin className="h-8 w-8 text-slate-400" />
                </div>
                <h2 className="text-2xl font-bold mb-4">No Providers Found</h2>
                <p className="text-slate-600 mb-6">Try adjusting your search filters or check back later.</p>
                <Button onClick={() => navigate('/partner')} variant="outline">
                  Become a Provider
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {providers.map(provider => (
                <Card key={provider.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/provider/${provider.id}`)}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{provider.name}</CardTitle>
                        <p className="text-sm text-slate-600">{provider.city}, {provider.state}</p>
                      </div>
                      {provider.verified && (
                        <Badge className="bg-emerald-100 text-emerald-800">
                          <Check className="h-3 w-3 mr-1" /> Verified
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <MapPin className="h-4 w-4" />
                        {provider.region}
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="h-4 w-4" />
                        {provider.phone}
                      </div>
                      {provider.services && provider.services.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {provider.services.map(s => (
                            <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </FeatureGate>
  );
};

// Provider Detail Page
const ProviderDetailPage = () => {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetchProvider();
  }, [providerId]);

  const fetchProvider = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/provider/${providerId}`);
      setProvider(response.data);
    } catch (error) {
      toast.error('Provider not found');
      navigate('/providers');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!provider) return null;

  return (
    <FeatureGate flag="FEATURE_PROVIDERS_ENABLED">
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-12">
          <Button variant="ghost" onClick={() => navigate('/providers')} className="mb-6">
            ← Back to Directory
          </Button>

          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-2xl">{provider.name}</CardTitle>
                  <p className="text-slate-600">{provider.city}, {provider.state}</p>
                </div>
                {provider.verified && (
                  <Badge className="bg-emerald-100 text-emerald-800">
                    <Check className="h-4 w-4 mr-1" /> Verified Provider
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {provider.description && (
                <p className="text-slate-700">{provider.description}</p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold">Contact Information</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      <span>{provider.address}, {provider.city}, {provider.state} {provider.zip_code}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-slate-400" />
                      <a href={`tel:${provider.phone}`} className="text-emerald-600 hover:underline">{provider.phone}</a>
                    </div>
                    {provider.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-slate-400" />
                        <a href={`mailto:${provider.email}`} className="text-emerald-600 hover:underline">{provider.email}</a>
                      </div>
                    )}
                    {provider.website && (
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-slate-400" />
                        <a href={provider.website} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">Visit Website</a>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold">Services Offered</h3>
                  {provider.service_details && provider.service_details.length > 0 ? (
                    <div className="space-y-2">
                      {provider.service_details.map(s => (
                        <div key={s.id} className="p-3 bg-slate-50 rounded-lg">
                          <div className="font-medium">{s.name}</div>
                          <p className="text-sm text-slate-600">{s.description}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500">Contact provider for available services</p>
                  )}
                </div>
              </div>

              {provider.booking_link && (
                <div className="pt-4 border-t">
                  <Button asChild className="w-full bg-emerald-600 hover:bg-emerald-700">
                    <a href={provider.booking_link} target="_blank" rel="noopener noreferrer">
                      Book Appointment
                    </a>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </FeatureGate>
  );
};

// Providers by State Page
const ProvidersByStatePage = () => {
  const { state } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ regions: [], providers: [] });
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetchData();
  }, [state]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/providers/state/${state}`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FeatureGate flag="FEATURE_PROVIDERS_ENABLED">
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 py-12">
          <Button variant="ghost" onClick={() => navigate('/providers')} className="mb-6">
            ← Back to Directory
          </Button>

          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">Providers in {state}</h1>
            <p className="text-slate-600">{data.providers.length} certified providers</p>
          </div>

          {/* Regions */}
          {data.regions.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4">Browse by Region</h2>
              <div className="flex flex-wrap gap-2">
                {data.regions.map(region => (
                  <Button
                    key={region}
                    variant="outline"
                    onClick={() => navigate(`/providers/${state}/${encodeURIComponent(region)}`)}
                  >
                    {region}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.providers.map(provider => (
                <Card key={provider.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/provider/${provider.id}`)}>
                  <CardHeader>
                    <CardTitle className="text-lg">{provider.name}</CardTitle>
                    <p className="text-sm text-slate-600">{provider.region}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone className="h-4 w-4" />
                      {provider.phone}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </FeatureGate>
  );
};

// Providers by Region Page
const ProvidersByRegionPage = () => {
  const { state, region } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ providers: [] });
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetchData();
  }, [state, region]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/providers/state/${state}/region/${encodeURIComponent(region)}`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FeatureGate flag="FEATURE_PROVIDERS_ENABLED">
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 py-12">
          <Button variant="ghost" onClick={() => navigate(`/providers/${state}`)} className="mb-6">
            ← Back to {state}
          </Button>

          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">{region}, {state}</h1>
            <p className="text-slate-600">{data.providers.length} certified providers</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {data.providers.map(provider => (
                <Card key={provider.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/provider/${provider.id}`)}>
                  <CardHeader>
                    <CardTitle className="text-lg">{provider.name}</CardTitle>
                    <p className="text-sm text-slate-600">{provider.address}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="h-4 w-4" />
                        {provider.phone}
                      </div>
                      {provider.services && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {provider.services.map(s => (
                            <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </FeatureGate>
  );
};

// Partner Program Landing Page
const PartnerProgramPage = () => {
  const navigate = useNavigate();

  return (
    <FeatureGate flag="FEATURE_PROVIDERS_ENABLED">
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 py-12">
          {/* Hero */}
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-emerald-100 text-emerald-800">Partner Program</Badge>
            <h1 className="text-5xl font-bold mb-6">Become a PepFactor Partner</h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8">
              Join our network of certified providers and grow your practice with cutting-edge peptide treatments.
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/partner/apply')} className="bg-emerald-600 hover:bg-emerald-700" size="lg">
                Apply Now
              </Button>
              <Button onClick={() => navigate('/territories')} variant="outline" size="lg">
                View Territories
              </Button>
            </div>
          </div>

          {/* Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📍</span>
                </div>
                <h3 className="font-bold text-lg mb-2">Exclusive Territories</h3>
                <p className="text-slate-600 text-sm">Secure your market with protected territories and exclusive access to local customers.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📚</span>
                </div>
                <h3 className="font-bold text-lg mb-2">Full Training & Support</h3>
                <p className="text-slate-600 text-sm">Comprehensive training programs and ongoing support to ensure your success.</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💰</span>
                </div>
                <h3 className="font-bold text-lg mb-2">Premium Margins</h3>
                <p className="text-slate-600 text-sm">Competitive pricing and high margins on premium peptide treatments.</p>
              </CardContent>
            </Card>
          </div>

          {/* Requirements */}
          <Card className="mb-16">
            <CardHeader>
              <CardTitle>Partnership Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-emerald-600" />
                    <span>Licensed medical spa or aesthetic practice</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-emerald-600" />
                    <span>Minimum 24 treatment kits per month commitment</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-emerald-600" />
                    <span>Dedicated treatment room for PepFactor services</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-emerald-600" />
                    <span>Staff willing to complete certification training</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-emerald-600" />
                    <span>Commitment to quality patient outcomes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-emerald-600" />
                    <span>Active marketing and promotion efforts</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="text-center bg-emerald-50 rounded-2xl p-12">
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-slate-600 mb-6">Complete our application form and we'll review your practice within 48 hours.</p>
            <Button onClick={() => navigate('/partner/apply')} className="bg-emerald-600 hover:bg-emerald-700" size="lg">
              Start Your Application
            </Button>
          </div>
        </div>
      </div>
    </FeatureGate>
  );
};

// Partner Application Page
const PartnerApplyPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [formData, setFormData] = useState({
    clinic_name: '',
    owner_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    region: '',
    website: '',
    services: [],
    monthly_commitment_kits: 24,
    notes: ''
  });

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, territoriesRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/partner/territories?status=Open`)
      ]);
      setServices(servicesRes.data);
      setTerritories(territoriesRes.data);
    } catch (error) {
      console.error('Failed to load data');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const submitData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'services') {
          submitData.append(key, value.join(','));
        } else {
          submitData.append(key, value);
        }
      });

      await axios.post(`${API}/partner/apply`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Application submitted successfully!');
      navigate('/partner');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  // Get unique states from territories
  const states = [...new Set(territories.map(t => t.state))].sort();
  const regions = territories.filter(t => t.state === formData.state).map(t => t.region_name);

  return (
    <FeatureGate flag="FEATURE_PROVIDERS_ENABLED">
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-12">
          <Button variant="ghost" onClick={() => navigate('/partner')} className="mb-6">
            ← Back to Partner Program
          </Button>

          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2">Partner Application</h1>
            <p className="text-slate-600">Complete the form below to apply for partnership.</p>
          </div>

          <Card>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Clinic Name *</label>
                    <Input
                      required
                      value={formData.clinic_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, clinic_name: e.target.value }))}
                      placeholder="Your Med Spa Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Owner Name *</label>
                    <Input
                      required
                      value={formData.owner_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, owner_name: e.target.value }))}
                      placeholder="Full Name"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Email *</label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="email@clinic.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone *</label>
                    <Input
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Street Address</label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="123 Main St"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">City</label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">State *</label>
                    <select
                      required
                      className="w-full p-2 border rounded-lg"
                      value={formData.state}
                      onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value, region: '' }))}
                    >
                      <option value="">Select State</option>
                      {states.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Region *</label>
                    <select
                      required
                      className="w-full p-2 border rounded-lg"
                      value={formData.region}
                      onChange={(e) => setFormData(prev => ({ ...prev, region: e.target.value }))}
                      disabled={!formData.state}
                    >
                      <option value="">Select Region</option>
                      {regions.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Website</label>
                  <Input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="https://yourclinic.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Services You Plan to Offer</label>
                  <div className="space-y-2">
                    {services.map(service => (
                      <label key={service.slug} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.services.includes(service.slug)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({ ...prev, services: [...prev.services, service.slug] }));
                            } else {
                              setFormData(prev => ({ ...prev, services: prev.services.filter(s => s !== service.slug) }));
                            }
                          }}
                          className="rounded"
                        />
                        <span>{service.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Monthly Kit Commitment</label>
                  <select
                    className="w-full p-2 border rounded-lg"
                    value={formData.monthly_commitment_kits}
                    onChange={(e) => setFormData(prev => ({ ...prev, monthly_commitment_kits: parseInt(e.target.value) }))}
                  >
                    <option value={24}>24 kits/month (Standard)</option>
                    <option value={48}>48 kits/month (Growth)</option>
                    <option value={72}>72 kits/month (Premium)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Additional Notes</label>
                  <textarea
                    className="w-full p-2 border rounded-lg min-h-[100px]"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Tell us about your practice and why you're interested in partnering..."
                  />
                </div>

                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Submit Application
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </FeatureGate>
  );
};

// Territories Page
const TerritoriesPage = () => {
  const navigate = useNavigate();
  const [territories, setTerritories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  React.useEffect(() => {
    fetchTerritories();
  }, []);

  const fetchTerritories = async () => {
    try {
      const response = await axios.get(`${API}/partner/territories`);
      setTerritories(response.data);
    } catch (error) {
      toast.error('Failed to load territories');
    } finally {
      setLoading(false);
    }
  };

  const states = [...new Set(territories.map(t => t.state))].sort();
  const filteredTerritories = filter 
    ? territories.filter(t => t.state === filter)
    : territories;

  const statusColors = {
    'Open': 'bg-green-100 text-green-800',
    'Reserved': 'bg-yellow-100 text-yellow-800',
    'Active': 'bg-blue-100 text-blue-800',
    'Waitlist': 'bg-slate-100 text-slate-800'
  };

  return (
    <FeatureGate flag="FEATURE_PROVIDERS_ENABLED">
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">Available Territories</h1>
            <p className="text-slate-600">Explore partnership opportunities in your region</p>
          </div>

          <div className="mb-6">
            <select
              className="p-2 border rounded-lg"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="">All States</option>
              {states.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredTerritories.map(territory => (
                <Card key={territory.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{territory.region_name}</CardTitle>
                        <p className="text-sm text-slate-600">{territory.state}</p>
                      </div>
                      <Badge className={statusColors[territory.status] || 'bg-slate-100'}>
                        {territory.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-slate-600 mb-4">
                      Capacity: {territory.current_partners}/{territory.capacity} partners
                    </div>
                    {territory.status === 'Open' && (
                      <Button 
                        onClick={() => navigate('/partner/apply')} 
                        className="w-full bg-emerald-600 hover:bg-emerald-700"
                        size="sm"
                      >
                        Apply for This Territory
                      </Button>
                    )}
                    {territory.status === 'Waitlist' && (
                      <Button 
                        onClick={() => navigate('/partner/apply')} 
                        variant="outline"
                        className="w-full"
                        size="sm"
                      >
                        Join Waitlist
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="mt-8 text-center">
            <p className="text-slate-600 mb-4">Don't see your region? Contact us to discuss expansion.</p>
            <Button onClick={() => navigate('/partner')} variant="outline">
              Learn More About Partnership
            </Button>
          </div>
        </div>
      </div>
    </FeatureGate>
  );
};

// Admin Provider Marketplace Page
const AdminProvidersPage = () => {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      const response = await axios.get(`${API}/admin/marketplace/providers`);
      setProviders(response.data);
    } catch (error) {
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (provider) => {
    try {
      const formData = new FormData();
      formData.append('active', !provider.active);
      await axios.put(`${API}/admin/marketplace/providers/${provider.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Provider ${provider.active ? 'deactivated' : 'activated'}`);
      fetchProviders();
    } catch (error) {
      toast.error('Failed to update provider');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Manage Providers</h1>
            <p className="text-slate-600">Provider marketplace directory management</p>
          </div>
          <Button onClick={() => navigate('/admin')} variant="outline">
            ← Back to Admin
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 font-medium">Provider</th>
                    <th className="text-left p-4 font-medium">Location</th>
                    <th className="text-left p-4 font-medium">Services</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {providers.map(provider => (
                    <tr key={provider.id} className="border-t">
                      <td className="p-4">
                        <div className="font-medium">{provider.name}</div>
                        <div className="text-sm text-slate-600">{provider.email}</div>
                      </td>
                      <td className="p-4">
                        <div>{provider.city}, {provider.state}</div>
                        <div className="text-sm text-slate-600">{provider.region}</div>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {provider.services?.map(s => (
                            <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge className={provider.active ? 'bg-green-100 text-green-800' : 'bg-slate-100'}>
                          {provider.active ? 'Active' : 'Inactive'}
                        </Badge>
                        {provider.verified && (
                          <Badge className="ml-1 bg-blue-100 text-blue-800">Verified</Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <Button size="sm" variant="ghost" onClick={() => toggleActive(provider)}>
                          {provider.active ? 'Deactivate' : 'Activate'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Admin Partners Page
const AdminPartnersPage = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  React.useEffect(() => {
    fetchPartners();
  }, [filter]);

  const fetchPartners = async () => {
    try {
      const url = filter ? `${API}/admin/partners?status=${filter}` : `${API}/admin/partners`;
      const response = await axios.get(url);
      setPartners(response.data);
    } catch (error) {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (partnerId, status) => {
    try {
      await axios.put(`${API}/admin/partners/${partnerId}/status?status=${status}`);
      toast.success(`Partner status updated to ${status}`);
      fetchPartners();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const statusColors = {
    'Applied': 'bg-yellow-100 text-yellow-800',
    'Approved': 'bg-blue-100 text-blue-800',
    'Active': 'bg-green-100 text-green-800',
    'AtRisk': 'bg-red-100 text-red-800',
    'Rejected': 'bg-slate-100 text-slate-800'
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Partner Applications</h1>
            <p className="text-slate-600">Manage partner program applications</p>
          </div>
          <Button onClick={() => navigate('/admin')} variant="outline">
            ← Back to Admin
          </Button>
        </div>

        <div className="mb-6">
          <select
            className="p-2 border rounded-lg"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="Applied">Applied</option>
            <option value="Approved">Approved</option>
            <option value="Active">Active</option>
            <option value="AtRisk">At Risk</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {partners.map(partner => (
              <Card key={partner.id}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-lg">{partner.clinic_name}</h3>
                      <p className="text-slate-600">{partner.owner_name}</p>
                      <div className="text-sm text-slate-500 mt-2">
                        {partner.email} • {partner.phone}
                      </div>
                      <div className="text-sm mt-1">
                        {partner.region}, {partner.state} • {partner.monthly_commitment_kits} kits/month
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge className={statusColors[partner.status]}>{partner.status}</Badge>
                      <div className="mt-4 space-x-2">
                        {partner.status === 'Applied' && (
                          <>
                            <Button size="sm" onClick={() => updateStatus(partner.id, 'Approved')} className="bg-emerald-600">
                              Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => updateStatus(partner.id, 'Rejected')}>
                              Reject
                            </Button>
                          </>
                        )}
                        {partner.status === 'Approved' && (
                          <Button size="sm" onClick={() => updateStatus(partner.id, 'Active')} className="bg-emerald-600">
                            Activate
                          </Button>
                        )}
                        {partner.status === 'Active' && (
                          <Button size="sm" variant="outline" onClick={() => updateStatus(partner.id, 'AtRisk')}>
                            Mark At Risk
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Admin Territories Page
const AdminTerritoriesPage = () => {
  const navigate = useNavigate();
  const [territories, setTerritories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newTerritory, setNewTerritory] = useState({ state: '', region_name: '', capacity: 1, status: 'Open' });

  React.useEffect(() => {
    fetchTerritories();
  }, []);

  const fetchTerritories = async () => {
    try {
      const response = await axios.get(`${API}/admin/territories`);
      setTerritories(response.data);
    } catch (error) {
      toast.error('Failed to load territories');
    } finally {
      setLoading(false);
    }
  };

  const createTerritory = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      Object.entries(newTerritory).forEach(([k, v]) => formData.append(k, v));
      await axios.post(`${API}/admin/territories`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Territory created');
      setShowForm(false);
      setNewTerritory({ state: '', region_name: '', capacity: 1, status: 'Open' });
      fetchTerritories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create territory');
    }
  };

  const updateStatus = async (territoryId, status) => {
    try {
      const formData = new FormData();
      formData.append('status', status);
      await axios.put(`${API}/admin/territories/${territoryId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Territory updated');
      fetchTerritories();
    } catch (error) {
      toast.error('Failed to update territory');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Manage Territories</h1>
            <p className="text-slate-600">Partner program territory management</p>
          </div>
          <div className="space-x-2">
            <Button onClick={() => setShowForm(!showForm)} className="bg-emerald-600 hover:bg-emerald-700">
              + Add Territory
            </Button>
            <Button onClick={() => navigate('/admin')} variant="outline">
              ← Back to Admin
            </Button>
          </div>
        </div>

        {showForm && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <form onSubmit={createTerritory} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input
                  placeholder="State"
                  value={newTerritory.state}
                  onChange={(e) => setNewTerritory(prev => ({ ...prev, state: e.target.value }))}
                  required
                />
                <Input
                  placeholder="Region Name"
                  value={newTerritory.region_name}
                  onChange={(e) => setNewTerritory(prev => ({ ...prev, region_name: e.target.value }))}
                  required
                />
                <Input
                  type="number"
                  placeholder="Capacity"
                  value={newTerritory.capacity}
                  onChange={(e) => setNewTerritory(prev => ({ ...prev, capacity: parseInt(e.target.value) }))}
                  min={1}
                />
                <Button type="submit" className="bg-emerald-600">Create</Button>
              </form>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 font-medium">Territory</th>
                    <th className="text-left p-4 font-medium">Capacity</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {territories.map(t => (
                    <tr key={t.id} className="border-t">
                      <td className="p-4">
                        <div className="font-medium">{t.region_name}</div>
                        <div className="text-sm text-slate-600">{t.state}</div>
                      </td>
                      <td className="p-4">{t.current_partners}/{t.capacity}</td>
                      <td className="p-4">
                        <select
                          className="p-1 border rounded text-sm"
                          value={t.status}
                          onChange={(e) => updateStatus(t.id, e.target.value)}
                        >
                          <option value="Open">Open</option>
                          <option value="Reserved">Reserved</option>
                          <option value="Active">Active</option>
                          <option value="Waitlist">Waitlist</option>
                        </select>
                      </td>
                      <td className="p-4 text-sm text-slate-600">
                        {t.assigned_clinic_ids?.length || 0} partners assigned
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Admin Feature Flags Page
const AdminFeatureFlagsPage = () => {
  const navigate = useNavigate();
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetchFlags();
  }, []);

  const fetchFlags = async () => {
    try {
      const response = await axios.get(`${API}/admin/feature-flags`);
      setFlags(response.data);
    } catch (error) {
      toast.error('Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  };

  const toggleFlag = async (flag) => {
    try {
      await axios.put(`${API}/admin/feature-flags/${flag.key}?value=${!flag.value}`);
      toast.success(`${flag.key} ${!flag.value ? 'enabled' : 'disabled'}`);
      fetchFlags();
    } catch (error) {
      toast.error('Failed to update flag');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Feature Flags</h1>
            <p className="text-slate-600">Enable or disable application features</p>
          </div>
          <Button onClick={() => navigate('/admin')} variant="outline">
            ← Back to Admin
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {flags.map(flag => (
              <Card key={flag.key}>
                <CardContent className="p-6 flex justify-between items-center">
                  <div>
                    <h3 className="font-bold">{flag.key}</h3>
                    <p className="text-sm text-slate-600">{flag.description || 'No description'}</p>
                  </div>
                  <Button
                    onClick={() => toggleFlag(flag)}
                    className={flag.value ? 'bg-emerald-600' : 'bg-slate-400'}
                  >
                    {flag.value ? 'Enabled' : 'Disabled'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== PRO_DEVICE LISTING PAGES (Feature Flag: FEATURE_DEVICES_ENABLED) ====================

const HOW_HEARD_OPTIONS = [
  { value: "referral", label: "Referral from colleague" },
  { value: "google", label: "Google search" },
  { value: "social_media", label: "Social media" },
  { value: "trade_show", label: "Trade show / Conference" },
  { value: "email_campaign", label: "Email campaign" },
  { value: "industry_publication", label: "Industry publication" },
  { value: "other", label: "Other" }
];

const PIPELINE_STAGES = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-800" },
  { value: "contacted", label: "Contacted", color: "bg-yellow-100 text-yellow-800" },
  { value: "qualified", label: "Qualified", color: "bg-purple-100 text-purple-800" },
  { value: "demo_scheduled", label: "Demo Scheduled", color: "bg-indigo-100 text-indigo-800" },
  { value: "proposal", label: "Proposal", color: "bg-orange-100 text-orange-800" },
  { value: "negotiation", label: "Negotiation", color: "bg-pink-100 text-pink-800" },
  { value: "won", label: "Won", color: "bg-green-100 text-green-800" },
  { value: "lost", label: "Lost", color: "bg-slate-100 text-slate-800" }
];

// Device Lead Form Modal
const DeviceLeadFormModal = ({ device, deviceListingId, onClose, leadType = "enquiry" }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business_name: '',
    postcode: '',
    state: '',
    how_heard: '',
    device_interest: device?.name || '',
    message: '',
    demo_datetime: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const submitData = new FormData();
      Object.entries(formData).forEach(([k, v]) => submitData.append(k, v));
      submitData.append('lead_type', leadType);
      submitData.append('device_listing_id', deviceListingId || '');
      submitData.append('source_url', window.location.href);

      await axios.post(`${API}/devices/lead`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(leadType === 'demo_request' 
        ? 'Demo request submitted! We will confirm your booking shortly.'
        : 'Enquiry submitted! Our team will contact you within 24-48 hours.');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const US_STATES = ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-lg my-8">
        <CardHeader>
          <CardTitle>{leadType === 'demo_request' ? 'Book a Demo' : 'Enquire Now'}</CardTitle>
          <p className="text-sm text-slate-600">{device?.name || 'PepFactor Pro Device'}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <Input required value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Phone *</label>
                <Input required value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <Input type="email" required value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Business Name *</label>
              <Input required value={formData.business_name} onChange={(e) => setFormData(prev => ({ ...prev, business_name: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Postcode *</label>
                <Input required value={formData.postcode} onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value }))} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">State *</label>
                <select required className="w-full p-2 border rounded-lg" value={formData.state} onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}>
                  <option value="">Select State</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">How did you hear about us? *</label>
              <select required className="w-full p-2 border rounded-lg" value={formData.how_heard} onChange={(e) => setFormData(prev => ({ ...prev, how_heard: e.target.value }))}>
                <option value="">Select option</option>
                {HOW_HEARD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {leadType === 'demo_request' && (
              <div>
                <label className="block text-sm font-medium mb-1">Preferred Demo Date/Time</label>
                <Input type="datetime-local" value={formData.demo_datetime} onChange={(e) => setFormData(prev => ({ ...prev, demo_datetime: e.target.value }))} />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Message (Optional)</label>
              <textarea className="w-full p-2 border rounded-lg min-h-[80px]" value={formData.message} onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))} placeholder="Tell us about your practice and goals..." />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Submit
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Device Listing Page (Sales Page Layout)
const DeviceListingPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadType, setLeadType] = useState('enquiry');

  React.useEffect(() => {
    fetchListing();
  }, [slug]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API}/devices/${slug}`);
      setListing(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Device listings are not yet available');
      } else {
        toast.error('Device not found');
      }
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const openLeadForm = (type) => {
    setLeadType(type);
    setShowLeadForm(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!listing) return null;

  const { hero, treatments, case_studies, testimonials, features, specifications, training, support_features, faqs, product } = listing;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">{hero.headline}</h1>
              <p className="text-xl text-slate-300 mb-8">{hero.subheadline}</p>
              {hero.highlights && hero.highlights.length > 0 && (
                <ul className="space-y-3 mb-8">
                  {hero.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-emerald-400 shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-4">
                <Button onClick={() => openLeadForm('demo_request')} size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  Book Demo
                </Button>
                <Button onClick={() => openLeadForm('enquiry')} size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  Enquire Now
                </Button>
              </div>
            </div>
            <div className="hidden lg:block">
              {hero.hero_image_url ? (
                <img src={hero.hero_image_url} alt={hero.headline} className="rounded-lg shadow-2xl" />
              ) : (
                <div className="bg-slate-700/50 rounded-lg aspect-video flex items-center justify-center">
                  <span className="text-6xl">🔬</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Treatments/Modalities Grid */}
      {treatments && treatments.length > 0 && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-4">Treatment Modalities</h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">Our integrated approach combines multiple modalities for superior results</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {treatments.map((t, i) => (
                <Card key={i} className="text-center">
                  <CardContent className="p-8">
                    <div className="text-5xl mb-4">{t.icon || '✨'}</div>
                    <h3 className="text-xl font-bold mb-3">{t.name}</h3>
                    <p className="text-slate-600">{t.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Proof Section - Case Studies */}
      {case_studies && case_studies.length > 0 && (
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-4">Clinical Results</h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">Real results from real patients</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {case_studies.map((cs, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <h3 className="font-bold text-lg mb-2">{cs.title}</h3>
                    <p className="text-slate-600 mb-4">{cs.description}</p>
                    <div className="flex gap-4 text-sm">
                      {cs.treatment_count && <Badge variant="outline">{cs.treatment_count} treatments</Badge>}
                      {cs.duration_weeks && <Badge variant="outline">{cs.duration_weeks} weeks</Badge>}
                      {cs.patient_type && <Badge variant="outline">{cs.patient_type}</Badge>}
                    </div>
                    {(cs.before_image_url || cs.after_image_url) && (
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div className="bg-slate-100 rounded-lg aspect-square flex items-center justify-center text-slate-400">Before</div>
                        <div className="bg-slate-100 rounded-lg aspect-square flex items-center justify-center text-slate-400">After</div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials && testimonials.length > 0 && (
        <section className="py-20 bg-emerald-50">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-12">What Practitioners Say</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {testimonials.map((t, i) => (
                <Card key={i} className="bg-white">
                  <CardContent className="p-8">
                    <div className="text-4xl text-emerald-300 mb-4">"</div>
                    <p className="text-lg mb-6 italic">{t.quote}</p>
                    <div>
                      <div className="font-bold">{t.author}</div>
                      <div className="text-slate-600 text-sm">{t.clinic}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* System Features */}
      {features && features.length > 0 && (
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-4">System Features</h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">Built for professional practice demands</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((f, i) => (
                <div key={i} className="text-center p-6">
                  <div className="text-4xl mb-4">{f.icon || '⚙️'}</div>
                  <h3 className="font-bold mb-2">{f.title}</h3>
                  <p className="text-slate-600 text-sm">{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Specifications Table */}
      {specifications && Object.keys(specifications).length > 0 && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-12">Technical Specifications</h2>
            <Card>
              <CardContent className="p-0">
                {Object.entries(specifications).map(([category, specs], i) => (
                  <div key={category}>
                    {i > 0 && <div className="border-t" />}
                    <div className="p-4 bg-slate-100 font-bold">{category}</div>
                    <table className="w-full">
                      <tbody>
                        {specs.map((spec, j) => (
                          <tr key={j} className={j % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="p-4 text-slate-600">{spec.label}</td>
                            <td className="p-4 font-medium">{spec.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Training & Support */}
      {training && (
        <section className="py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold mb-4">{training.title}</h2>
                <p className="text-slate-600 mb-8">{training.description}</p>
                {training.included_items && training.included_items.length > 0 && (
                  <ul className="space-y-3">
                    {training.included_items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h3 className="text-xl font-bold mb-6">Ongoing Support</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {support_features && support_features.map((sf, i) => (
                    <div key={i} className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                      <Check className="h-5 w-5 text-emerald-600" />
                      <span className="text-sm">{sf}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs && faqs.length > 0 && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <h3 className="font-bold mb-2">{faq.question}</h3>
                    <p className="text-slate-600">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">Ready to Transform Your Practice?</h2>
          <p className="text-xl text-emerald-100 mb-8">Schedule a personalized demo or speak with our team today.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button onClick={() => openLeadForm('demo_request')} size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50">
              Book Demo
            </Button>
            <Button onClick={() => openLeadForm('enquiry')} size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
              Enquire Now
            </Button>
          </div>
        </div>
      </section>

      {/* Lead Form Modal */}
      {showLeadForm && (
        <DeviceLeadFormModal 
          device={product} 
          deviceListingId={listing.id} 
          onClose={() => setShowLeadForm(false)} 
          leadType={leadType} 
        />
      )}
    </div>
  );
};

// Devices Directory Page
const DevicesDirectoryPage = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API}/devices`);
      setListings(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        // Feature not enabled - show coming soon
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <FeatureGate flag="FEATURE_DEVICES_ENABLED">
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="mb-8 text-center">
            <h1 className="text-4xl font-bold mb-4">Professional Devices</h1>
            <p className="text-slate-600 max-w-2xl mx-auto">Explore our range of professional-grade treatment systems designed for modern aesthetic practices.</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            </div>
          ) : listings.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-3xl">🔬</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">Coming Soon</h2>
                <p className="text-slate-600">Professional device listings will be available soon.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {listings.map(listing => (
                <Card key={listing.id} className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/devices/${listing.slug}`)}>
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white p-8">
                    <h2 className="text-2xl font-bold mb-2">{listing.hero?.headline || listing.product?.name}</h2>
                    <p className="text-slate-300 line-clamp-2">{listing.hero?.subheadline}</p>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {listing.treatments?.slice(0, 3).map((t, i) => (
                        <Badge key={i} variant="outline">{t.name}</Badge>
                      ))}
                    </div>
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </FeatureGate>
  );
};

// Admin - Device Listings Management
const AdminDeviceListingsPage = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API}/admin/device-listings`);
      setListings(response.data);
    } catch (error) {
      toast.error('Failed to load device listings');
    } finally {
      setLoading(false);
    }
  };

  const togglePublished = async (listing) => {
    try {
      await axios.put(`${API}/admin/device-listings/${listing.id}`, { published: !listing.published });
      toast.success(`Listing ${listing.published ? 'unpublished' : 'published'}`);
      fetchListings();
    } catch (error) {
      toast.error('Failed to update listing');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Device Listings</h1>
            <p className="text-slate-600">Manage PRO_DEVICE sales pages</p>
          </div>
          <Button onClick={() => navigate('/admin')} variant="outline">← Back to Admin</Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 font-medium">Device</th>
                    <th className="text-left p-4 font-medium">Slug</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {listings.map(listing => (
                    <tr key={listing.id} className="border-t">
                      <td className="p-4">
                        <div className="font-medium">{listing.hero?.headline || listing.product_name}</div>
                        <div className="text-sm text-slate-600">{listing.product_name}</div>
                      </td>
                      <td className="p-4 font-mono text-sm">/devices/{listing.slug}</td>
                      <td className="p-4">
                        <Badge className={listing.published ? 'bg-green-100 text-green-800' : 'bg-slate-100'}>
                          {listing.published ? 'Published' : 'Draft'}
                        </Badge>
                      </td>
                      <td className="p-4 space-x-2">
                        <Button size="sm" variant="outline" onClick={() => window.open(`/devices/${listing.slug}`, '_blank')}>Preview</Button>
                        <Button size="sm" variant="ghost" onClick={() => togglePublished(listing)}>
                          {listing.published ? 'Unpublish' : 'Publish'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {listings.length === 0 && (
                    <tr><td colSpan={4} className="p-8 text-center text-slate-500">No device listings yet</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

// Admin - Device Leads Pipeline
const AdminDeviceLeadsPage = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);

  React.useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/device-leads${filter ? `?pipeline_stage=${filter}` : ''}`),
        axios.get(`${API}/admin/device-leads/pipeline-stats`)
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStage = async (leadId, stage) => {
    try {
      await axios.put(`${API}/admin/device-leads/${leadId}?pipeline_stage=${stage}`);
      toast.success('Lead updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const getStageColor = (stage) => {
    const found = PIPELINE_STAGES.find(s => s.value === stage);
    return found?.color || 'bg-slate-100';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Device Leads Pipeline</h1>
            <p className="text-slate-600">Manage incoming device enquiries and demo requests</p>
          </div>
          <Button onClick={() => navigate('/admin')} variant="outline">← Back to Admin</Button>
        </div>

        {/* Pipeline Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
            {PIPELINE_STAGES.map(stage => (
              <Card key={stage.value} className={`cursor-pointer ${filter === stage.value ? 'ring-2 ring-emerald-500' : ''}`} onClick={() => setFilter(filter === stage.value ? '' : stage.value)}>
                <CardContent className="p-4 text-center">
                  <div className="text-2xl font-bold">{stats.stages[stage.value] || 0}</div>
                  <div className="text-xs text-slate-600">{stage.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 font-medium">Contact</th>
                    <th className="text-left p-4 font-medium">Business</th>
                    <th className="text-left p-4 font-medium">Device</th>
                    <th className="text-left p-4 font-medium">Type</th>
                    <th className="text-left p-4 font-medium">Stage</th>
                    <th className="text-left p-4 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(lead => (
                    <tr key={lead.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedLead(lead)}>
                      <td className="p-4">
                        <div className="font-medium">{lead.name}</div>
                        <div className="text-sm text-slate-600">{lead.email}</div>
                      </td>
                      <td className="p-4">
                        <div>{lead.business_name}</div>
                        <div className="text-sm text-slate-600">{lead.state}</div>
                      </td>
                      <td className="p-4 text-sm">{lead.device_interest}</td>
                      <td className="p-4">
                        <Badge variant="outline">{lead.lead_type === 'demo_request' ? 'Demo' : 'Enquiry'}</Badge>
                      </td>
                      <td className="p-4">
                        <select className={`p-1 rounded text-xs ${getStageColor(lead.pipeline_stage)}`} value={lead.pipeline_stage} onClick={(e) => e.stopPropagation()} onChange={(e) => updateLeadStage(lead.id, e.target.value)}>
                          {PIPELINE_STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </td>
                      <td className="p-4 text-sm text-slate-600">
                        {new Date(lead.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                  {leads.length === 0 && (
                    <tr><td colSpan={6} className="p-8 text-center text-slate-500">No leads yet</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Lead Detail Modal */}
        {selectedLead && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{selectedLead.name}</CardTitle>
                    <p className="text-slate-600">{selectedLead.business_name}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedLead(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-600">Email</label>
                    <div>{selectedLead.email}</div>
                  </div>
                  <div>
                    <label className="text-sm text-slate-600">Phone</label>
                    <div>{selectedLead.phone}</div>
                  </div>
                  <div>
                    <label className="text-sm text-slate-600">Location</label>
                    <div>{selectedLead.postcode}, {selectedLead.state}</div>
                  </div>
                  <div>
                    <label className="text-sm text-slate-600">How Heard</label>
                    <div>{HOW_HEARD_OPTIONS.find(o => o.value === selectedLead.how_heard)?.label || selectedLead.how_heard}</div>
                  </div>
                  <div>
                    <label className="text-sm text-slate-600">Device Interest</label>
                    <div>{selectedLead.device_interest}</div>
                  </div>
                  <div>
                    <label className="text-sm text-slate-600">Lead Type</label>
                    <div>{selectedLead.lead_type === 'demo_request' ? 'Demo Request' : 'Enquiry'}</div>
                  </div>
                </div>
                {selectedLead.message && (
                  <div>
                    <label className="text-sm text-slate-600">Message</label>
                    <div className="bg-slate-50 p-3 rounded mt-1">{selectedLead.message}</div>
                  </div>
                )}
                {selectedLead.demo_scheduled && (
                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <div className="font-medium text-indigo-800">Demo Scheduled</div>
                    <div className="text-indigo-600">
                      {selectedLead.demo_datetime ? new Date(selectedLead.demo_datetime).toLocaleString() : 'TBD'}
                      {selectedLead.demo_type && ` (${selectedLead.demo_type})`}
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-sm text-slate-600">Pipeline Stage</label>
                  <select className="w-full p-2 border rounded-lg mt-1" value={selectedLead.pipeline_stage} onChange={(e) => { updateLeadStage(selectedLead.id, e.target.value); setSelectedLead({...selectedLead, pipeline_stage: e.target.value}); }}>
                    {PIPELINE_STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

// Admin Users Page
const AdminPortalUsersPage = () => {
  const { adminToken, adminUser } = useAdminAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    // Only ADMIN can access this page
    // Wait for adminUser to be loaded before checking
    if (adminUser && adminUser.role !== 'ADMIN') {
      toast.error('Access denied. Admin role required.');
      navigate('/admin/portal/products');
    }
  }, [adminUser, navigate]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter) params.append('role', roleFilter);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/admin/users?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminUser && adminUser.role === 'ADMIN') {
      fetchUsers();
    }
  }, [adminUser, roleFilter]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}`, { role: newRole }, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      toast.success('User role updated');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.patch(`${API}/admin/users/${user.id}`, { is_active: !user.is_active }, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      toast.success(user.is_active ? 'User disabled' : 'User enabled');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      await axios.post(`${API}/admin/users`, userData, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      toast.success('User created');
      setShowForm(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      ADMIN: 'bg-purple-100 text-purple-700',
      STAFF_ADMIN: 'bg-blue-100 text-blue-700',
      PARTNER_USER: 'bg-emerald-100 text-emerald-700',
      CUSTOMER: 'bg-slate-100 text-slate-700'
    };
    return <Badge className={styles[role] || styles.CUSTOMER}>{role}</Badge>;
  };

  // Wait for adminUser to be loaded and check role
  if (!adminUser) {
    return (
      <AdminLayout currentPage="users">
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      </AdminLayout>
    );
  }
  
  if (adminUser.role !== 'ADMIN') return null;

  return (
    <AdminLayout currentPage="users">
      <div className="p-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Users</h1>
            <p className="text-slate-600 mt-1">Manage user accounts and roles</p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            New User
          </Button>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-4 items-end">
          <div className="flex-1 max-w-md">
            <Label className="text-sm text-slate-600 mb-1 block">Search</Label>
            <div className="flex gap-2">
              <Input
                placeholder="Search by email or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchUsers()}
              />
              <Button variant="outline" onClick={fetchUsers}>
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="w-48">
            <Label className="text-sm text-slate-600 mb-1 block">Role</Label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">All Roles</option>
              <option value="ADMIN">Admin</option>
              <option value="STAFF_ADMIN">Staff Admin</option>
              <option value="PARTNER_USER">Partner User</option>
              <option value="CUSTOMER">Customer</option>
            </select>
          </div>
        </div>

        {/* Create User Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md m-4">
              <CardHeader>
                <CardTitle>Create New User</CardTitle>
              </CardHeader>
              <CardContent>
                <CreateUserForm onSave={handleCreateUser} onCancel={() => setShowForm(false)} />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-slate-600">User</th>
                  <th className="text-left p-4 font-medium text-slate-600">Role</th>
                  <th className="text-left p-4 font-medium text-slate-600">Status</th>
                  <th className="text-left p-4 font-medium text-slate-600">Created</th>
                  <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map(user => (
                  <tr key={user.id} className="hover:bg-slate-50">
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-slate-900">{user.name || 'No name'}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <select
                        value={user.role}
                        onChange={(e) => handleRoleChange(user.id, e.target.value)}
                        disabled={user.id === adminUser?.id}
                        className="px-2 py-1 rounded text-sm border bg-white"
                      >
                        <option value="ADMIN">Admin</option>
                        <option value="STAFF_ADMIN">Staff Admin</option>
                        <option value="PARTNER_USER">Partner User</option>
                        <option value="CUSTOMER">Customer</option>
                      </select>
                    </td>
                    <td className="p-4">
                      <Badge className={user.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                        {user.is_active !== false ? 'Active' : 'Disabled'}
                      </Badge>
                    </td>
                    <td className="p-4 text-slate-600 text-sm">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="p-4 text-right space-x-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          try {
                            const response = await axios.post(`${API}/admin/users/${user.id}/password/reset-link`, {}, {
                              headers: { 'Authorization': `Bearer ${adminToken}` }
                            });
                            toast.success(`Reset link sent to ${user.email}`);
                            // In production, the link would be emailed. For now, log it.
                            console.log('Reset URL:', response.data.reset_url);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to send reset link');
                          }
                        }}
                        disabled={user.id === adminUser?.id}
                        className="text-blue-600"
                      >
                        Reset Password
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleActive(user)}
                        disabled={user.id === adminUser?.id}
                        className={user.is_active !== false ? 'text-red-600' : 'text-emerald-600'}
                      >
                        {user.is_active !== false ? 'Disable' : 'Enable'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No users found.
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="mt-6 grid grid-cols-4 gap-4">
          <Card className="p-4">
            <p className="text-sm text-slate-500">Total Users</p>
            <p className="text-2xl font-bold">{users.length}</p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-slate-500">Admins</p>
            <p className="text-2xl font-bold text-purple-600">
              {users.filter(u => u.role === 'ADMIN' || u.role === 'STAFF_ADMIN').length}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-slate-500">Partners</p>
            <p className="text-2xl font-bold text-emerald-600">
              {users.filter(u => u.role === 'PARTNER_USER').length}
            </p>
          </Card>
          <Card className="p-4">
            <p className="text-sm text-slate-500">Customers</p>
            <p className="text-2xl font-bold text-slate-600">
              {users.filter(u => u.role === 'CUSTOMER').length}
            </p>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

// Create User Form
const CreateUserForm = ({ onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'CUSTOMER'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Email *</Label>
        <Input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          required
        />
      </div>
      <div>
        <Label>Password * (min 8 characters)</Label>
        <Input
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({...formData, password: e.target.value})}
          required
          minLength={8}
        />
      </div>
      <div>
        <Label>Name</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
        />
      </div>
      <div>
        <Label>Role</Label>
        <select
          value={formData.role}
          onChange={(e) => setFormData({...formData, role: e.target.value})}
          className="flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm"
        >
          <option value="CUSTOMER">Customer</option>
          <option value="PARTNER_USER">Partner User</option>
          <option value="STAFF_ADMIN">Staff Admin</option>
          <option value="ADMIN">Admin</option>
        </select>
      </div>
      <div className="flex gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
        <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
          Create User
        </Button>
      </div>
    </form>
  );
};

// Admin Audit Log Page
const AdminPortalAuditPage = () => {
  const { adminToken } = useAdminAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityFilter, setEntityFilter] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (entityFilter) params.append('entity_type', entityFilter);
      params.append('limit', '100');
      
      const response = await axios.get(`${API}/admin/audit?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [entityFilter]);

  const getActionBadge = (action) => {
    const styles = {
      CREATE: 'bg-emerald-100 text-emerald-700',
      UPDATE: 'bg-blue-100 text-blue-700',
      DELETE: 'bg-red-100 text-red-700',
      STATUS_CHANGE: 'bg-amber-100 text-amber-700',
      VISIBILITY_CHANGE: 'bg-purple-100 text-purple-700',
      PASSWORD_RESET: 'bg-pink-100 text-pink-700'
    };
    return <Badge className={styles[action] || 'bg-slate-100 text-slate-700'}>{action}</Badge>;
  };

  return (
    <AdminLayout currentPage="audit">
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Audit Log</h1>
          <p className="text-slate-600 mt-1">Track all changes made in the admin portal</p>
        </div>

        {/* Filter */}
        <div className="mb-6 flex gap-4 items-end">
          <div className="w-48">
            <Label className="text-sm text-slate-600 mb-1 block">Entity Type</Label>
            <select
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">All Entities</option>
              <option value="product">Products</option>
              <option value="user">Users</option>
            </select>
          </div>
          <Button variant="outline" onClick={fetchLogs}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Logs Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-slate-600">Timestamp</th>
                  <th className="text-left p-4 font-medium text-slate-600">Actor</th>
                  <th className="text-left p-4 font-medium text-slate-600">Action</th>
                  <th className="text-left p-4 font-medium text-slate-600">Entity</th>
                  <th className="text-left p-4 font-medium text-slate-600">Changes</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-slate-50">
                    <td className="p-4 text-sm text-slate-600">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="p-4">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{log.actor_email}</p>
                        <p className="text-xs text-slate-500">{log.actor_role}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      {getActionBadge(log.action)}
                    </td>
                    <td className="p-4">
                      <div>
                        <p className="text-sm font-medium text-slate-900 capitalize">{log.entity_type}</p>
                        <p className="text-xs text-slate-500 font-mono">{log.entity_id?.slice(0, 8)}...</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="text-sm text-slate-600 max-w-xs">
                        {Object.entries(log.changed_fields || {}).map(([field, change]) => (
                          <div key={field} className="truncate">
                            <span className="font-medium">{field}:</span>{' '}
                            {typeof change === 'object' ? (
                              <span>
                                <span className="text-red-600">{String(change.from)}</span>
                                {' → '}
                                <span className="text-emerald-600">{String(change.to)}</span>
                              </span>
                            ) : (
                              String(change)
                            )}
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {logs.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No audit logs yet. Changes made in the admin portal will appear here.
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// ==================== MAIN APP ====================

function App() {
  return (
    <AdminAuthProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            <Toaster position="top-right" />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/auth" element={<AuthPage />} />
              
              {/* Admin Portal Routes */}
              <Route path="/admin/portal/login" element={<AdminPortalLoginPage />} />
              <Route path="/admin/portal/forgot-password" element={<AdminForgotPasswordPage />} />
              <Route path="/admin/portal/reset-password" element={<AdminResetPasswordPage />} />
              <Route path="/admin/portal/products" element={<AdminPortalProductsPage />} />
              <Route path="/admin/portal/users" element={<AdminPortalUsersPage />} />
              <Route path="/admin/portal/audit" element={<AdminPortalAuditPage />} />
              
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/shop/:productId" element={<ProductDetailPage />} />
              <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
              <Route path="/capture" element={<ProtectedRoute><CapturePage /></ProtectedRoute>} />
              <Route path="/results" element={<ProtectedRoute><ResultsPage /></ProtectedRoute>} />
              <Route path="/analysis/professional" element={<ProtectedRoute><ProfessionalAnalysisPage /></ProtectedRoute>} />
              <Route path="/progress" element={<ProtectedRoute><PhotoGalleryPage /></ProtectedRoute>} />
              <Route path="/progress/compare" element={<ProtectedRoute><PhotoComparisonPage /></ProtectedRoute>} />
              <Route path="/plan" element={<ProtectedRoute><PlanPage /></ProtectedRoute>} />
              <Route path="/book" element={<ProtectedRoute><BookPage /></ProtectedRoute>} />
              <Route path="/book/:clinicId" element={<ProtectedRoute><ClinicDetailPage /></ProtectedRoute>} />
              <Route path="/subscriptions" element={<ProtectedRoute><SubscriptionsPage /></ProtectedRoute>} />
              <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
              <Route path="/clinic-signup" element={<ProtectedRoute><ClinicSignupPage /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboardPage /></ProtectedRoute>} />
              <Route path="/admin/products" element={<ProtectedRoute><AdminProductsPage /></ProtectedRoute>} />
              <Route path="/admin/products" element={<ProtectedRoute><AdminProductsPage /></ProtectedRoute>} />
              <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
              <Route path="/receipt" element={<ProtectedRoute><ReceiptPage /></ProtectedRoute>} />
              <Route path="/email-preview" element={<ProtectedRoute><EmailPreviewPage /></ProtectedRoute>} />
              
              {/* Provider Onboarding (Private) */}
              <Route path="/pep-provider/onboarding" element={<ProviderOnboardingPage />} />
              <Route path="/pep-provider/success" element={<ProviderSuccessPage />} />
              
              {/* Provider Portal */}
              <Route path="/provider" element={<ProviderPortalPage />} />
              <Route path="/provider/apply" element={<ProviderApplyPage />} />
              <Route path="/provider/login" element={<ProviderLoginPage />} />
              <Route path="/provider/order" element={<ProtectedRoute><ProviderOrderPage /></ProtectedRoute>} />
              <Route path="/provider/profile" element={<ProtectedRoute><ProviderProfilePage /></ProtectedRoute>} />
              <Route path="/provider/leads" element={<ProtectedRoute><ProviderLeadsPage /></ProtectedRoute>} />
              <Route path="/provider/help" element={<ProviderHelpPage />} />
              
              {/* Provider Marketplace */}
              <Route path="/providers" element={<ProvidersDirectoryPage />} />
              <Route path="/provider/:providerId" element={<ProviderDetailPage />} />
              <Route path="/providers/:state" element={<ProvidersByStatePage />} />
              <Route path="/providers/:state/:region" element={<ProvidersByRegionPage />} />
              
              {/* Partner Program */}
              <Route path="/partner" element={<PartnerProgramPage />} />
              <Route path="/partner/apply" element={<PartnerApplyPage />} />
              <Route path="/territories" element={<TerritoriesPage />} />
              
              {/* Admin Marketplace */}
              <Route path="/admin/providers" element={<ProtectedRoute><AdminProvidersPage /></ProtectedRoute>} />
              <Route path="/admin/partners" element={<ProtectedRoute><AdminPartnersPage /></ProtectedRoute>} />
              <Route path="/admin/territories" element={<ProtectedRoute><AdminTerritoriesPage /></ProtectedRoute>} />
              <Route path="/admin/feature-flags" element={<ProtectedRoute><AdminFeatureFlagsPage /></ProtectedRoute>} />
              
              {/* PRO_DEVICE Listings (Feature Flag: FEATURE_DEVICES_ENABLED) */}
              <Route path="/devices" element={<DevicesDirectoryPage />} />
              <Route path="/devices/:slug" element={<DeviceListingPage />} />
              <Route path="/admin/device-listings" element={<ProtectedRoute><AdminDeviceListingsPage /></ProtectedRoute>} />
              <Route path="/admin/device-leads" element={<ProtectedRoute><AdminDeviceLeadsPage /></ProtectedRoute>} />
            </Routes>
          </div>
        </BrowserRouter>
      </AuthProvider>
    </AdminAuthProvider>
  );
}

export default App;
